import DateRangeControl from '../common/DateRangeControl';
import { validRange } from '../../utils/dateRanges';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Divider,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  Menu,
  Skeleton,
} from '@mui/material';
import {
  Schedule,
  CheckCircle,
  Error as ErrorIcon,
  DataUsage,
  GetApp,
  Info,
  InfoOutlined,
  Apple,
  Cloud,
  OpenInNew,
  Visibility,
  Delete,
  ExpandMore,
  Refresh,
} from '@mui/icons-material';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { apiService } from '../../config/api';
import { analyticsService } from '../../services/analyticsService';
import {
  saveDashboardSession,
  loadDashboardSession,
} from '../../utils/urlParamUtils';
import { getDeviceType, deviceTypeMatchesSource } from '../../utils/deviceType';
import {
  getFitbitExtractionProgressPresentation,
  getFitbitExtractionStatusSemantics,
  shouldRemoveFitbitExtractionJobLocally,
  waitForFitbitExtractionJobId,
} from '../../utils/fitbitExtractionStatus';

interface Participant {
  id: string; // Document ID - unique
  participantId: string;
  participantEmail?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  authorizationStatus: boolean;
  hasTokens: boolean;
  fitbitCollectionEnrollment?: boolean;
  dataSource?: 'fitbit' | 'healthkit' | 'both';
  fitbitUserId?: string;
  fitbitData?: {
    accessToken?: string;
    expiresAt?: string;
  };
  fitbitAccessToken?: string;
  fitbitTokens?: {
    accessToken?: string;
    expiresAt?: string;
  };
  healthkit?: {
    authorized?: boolean;
    authorizedAt?: string;
    lastSyncAt?: string;
  };
  enrolledAt?: string; // ISO date string
  historicalExtraction?: {
    status: 'in_progress' | 'completed' | 'failed';
    lastExtractedDate?: string; // YYYY-MM-DD format
    earliestDateFound?: string; // YYYY-MM-DD format
    memberSince?: string; // YYYY-MM-DD format
    startedAt?: string;
    completedAt?: string;
    totalDaysExtracted?: number;
    consecutiveNoDataDays?: number;
    error?: string;
    lastRunAt?: string;
  };
}

interface FailedTypeEntry {
  type: string;
  reason?: string;
}

interface DataExtractionJob {
  id: string;
  participantId: string;
  extractionName: string;
  dataTypes: string[];
  dateRange: DateRangeSelection;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  createdAt: Date;
  completedAt?: Date;
  error?: string;
  jobId?: string; // Backend job ID for progress tracking
  clientRequestId?: string; // Correlates the optimistic row with the durable Fitbit job
  backendStatus?: string; // Raw backend phase used for truthful progress presentation
  progressMessage?: string; // Current progress message
  currentDate?: string; // Current date being processed
  currentDataDate?: string; // Earliest date we have data for
  processedDates?: number; // Completed units in the current phase
  totalDates?: number; // Total units in the current phase
  fileInfo?: { fileName?: string; bucketName?: string; fileUrl?: string; size?: number } | null; // Set when completed (Fitbit)
  // Backend may report per-type serialization failures — surfaced as a banner.
  failedTypes?: FailedTypeEntry[];
  totalRecords?: number;
}

interface DateRangeSelection {
  startDate: Date | null;
  endDate: Date | null;
  mode: 'single' | 'range';
}

interface DataExtractionInterfaceProps {
  studyId: string;
  studyName: string;
  participants: Participant[];
  onRefreshParticipants: () => void;
  source?: 'fitbit' | 'healthkit';
}

const ExtractionJobProgressIndicator: React.FC<{
  job: DataExtractionJob;
  compact?: boolean;
}> = ({ job, compact = false }) => {
  const presentation = getFitbitExtractionProgressPresentation({
    backendStatus: job.backendStatus,
    backendPercentage: job.progress,
    processedDates: job.processedDates,
    totalDates: job.totalDates,
    progressMessage: job.progressMessage,
  });
  const targetValue = presentation.variant === 'determinate'
    ? presentation.value ?? 0
    : null;
  const [displayedValue, setDisplayedValue] = useState(() =>
    targetValue !== null && targetValue > 0 ? 1 : 0
  );

  useEffect(() => {
    if (targetValue === null) {
      setDisplayedValue(0);
      return undefined;
    }

    setDisplayedValue(current => {
      if (current > targetValue) return targetValue;
      if (current === 0 && targetValue > 0) return 1;
      return current;
    });

    const timer = setInterval(() => {
      setDisplayedValue(current => {
        if (current >= targetValue) {
          clearInterval(timer);
          return current;
        }
        return current + 1;
      });
    }, 80);

    return () => clearInterval(timer);
  }, [targetValue]);

  return (
    <Box mt={compact ? 0.75 : 0}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap title={presentation.label} sx={{ minWidth: 0 }}>
          {presentation.label}
        </Typography>
        {targetValue !== null && (
          <Typography variant="caption" color="primary" fontWeight="bold" sx={{ flexShrink: 0 }}>
            {displayedValue}%
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant={presentation.variant}
        value={targetValue === null ? undefined : displayedValue}
        aria-label={presentation.label}
        sx={{
          height: compact ? 6 : 8,
          borderRadius: 2,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { borderRadius: 2 },
        }}
      />
    </Box>
  );
};

type HistoricalAvailabilityStatus =
  | 'historical_complete'
  | 'historical_in_progress'
  | 'historical_unknown';

const getLatestExtractableDate = (): string => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().split('T')[0];
};

const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const deriveParticipantHistoricalAvailability = (participant: Participant) => {
  const hist = participant.historicalExtraction;
  const lastExtractedDate = hist?.lastExtractedDate;
  const earliestDateFound = hist?.earliestDateFound;
  const enrolledAtDate = participant.enrolledAt
    ? String(participant.enrolledAt).split('T')[0]
    : null;
  const endDate = getLatestExtractableDate();

  let startDate = earliestDateFound ?? lastExtractedDate ?? enrolledAtDate ?? endDate;
  let status: HistoricalAvailabilityStatus = 'historical_unknown';

  if (hist?.status === 'completed') {
    startDate = earliestDateFound ?? lastExtractedDate ?? enrolledAtDate ?? endDate;
    status = 'historical_complete';
  } else if (hist?.status === 'in_progress') {
    startDate = lastExtractedDate ?? earliestDateFound ?? enrolledAtDate ?? endDate;
    status = 'historical_in_progress';
  }

  return {
    startDate,
    endDate,
    status,
  };
};

const buildHistoricalAvailabilityLine = (participant: Participant): string => {
  const { startDate, endDate, status } = deriveParticipantHistoricalAvailability(participant);
  const rangeLine = `Data available from ${formatDateShort(startDate)} to ${formatDateShort(endDate)}`;

  if (status === 'historical_in_progress') {
    return `Historical extraction in progress. ${rangeLine}`;
  }

  if (status === 'historical_complete') {
    return `Historical extraction complete. ${rangeLine}`;
  }

  return rangeLine;
};

const getParticipantDeviceSource = (
  participant: Participant
): 'fitbit' | 'healthkit' | 'both' | 'none' => getDeviceType(participant);

const toDateInputValue = (date: Date): string => date.toISOString().split('T')[0];

const toUtcDate = (dateStr: string): Date => new Date(`${dateStr}T00:00:00Z`);

// Available Fitbit data types - metrics from optimized pipeline (activity, heart, sleep, HRV, SpO2 & breathing)
// Organized into sections
const FITBIT_DATA_TYPES = [
  // ═══════════════════════════════════════════════════════════════
  // SECTION 1: PHYSICAL ACTIVITY (6 metrics - Request 1)
  // ═══════════════════════════════════════════════════════════════
  { value: 'activity_summary_steps', label: 'Steps (Daily Total)', description: 'Total steps per day', section: 'Physical Activity' },
  { value: 'activity_summary_calories', label: 'Calories Burned (Daily Total)', description: 'Total calories burned per day', section: 'Physical Activity' },
  { value: 'activity_summary_distance', label: 'Distance (Daily Total)', description: 'Total distance traveled per day (km)', section: 'Physical Activity' },
  { value: 'activity_summary_light_minutes', label: 'Active Minutes - Light', description: 'Time spent in light activity per day', section: 'Physical Activity' },
  { value: 'activity_summary_moderate_minutes', label: 'Active Minutes - Moderate', description: 'Time spent in moderate activity per day', section: 'Physical Activity' },
  { value: 'activity_summary_vigorous_minutes', label: 'Active Minutes - Vigorous', description: 'Time spent in vigorous activity per day', section: 'Physical Activity' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 2: CARDIOVASCULAR (3 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'heartrate_summary_resting', label: 'Resting Heart Rate', description: 'Daily resting heart rate (bpm)', section: 'Cardiovascular' },
  { value: 'heartrate_intraday_1sec', label: 'Heart Rate Intraday (1-second)', description: 'High-resolution heart rate data throughout the day', section: 'Cardiovascular' },
  { value: 'hrv_summary', label: 'Heart Rate Variability (RMSSD)', description: 'Daily HRV RMSSD value measured during sleep (ms)', section: 'Cardiovascular' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 3: SLEEP & REST (4 metrics - Request 3)
  // ═══════════════════════════════════════════════════════════════
  { value: 'sleep_duration', label: 'Sleep Duration', description: 'Total sleep time per night (minutes)', section: 'Sleep & Rest' },
  { value: 'sleep_efficiency', label: 'Sleep Efficiency', description: 'Sleep quality percentage', section: 'Sleep & Rest' },
  { value: 'sleep_stages', label: 'Sleep Stages', description: 'Detailed sleep phases (light, deep, REM)', section: 'Sleep & Rest' },
  { value: 'sleep_schedule', label: 'Sleep Schedule', description: 'Bedtime and wake time per night', section: 'Sleep & Rest' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 4: SpO2 & BREATHING (4 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'spo2_avg', label: 'SpO2 Summary', description: 'Daily blood oxygen summary (avg, min, max)', section: 'SpO2 & Breathing' },
  { value: 'spo2_intraday', label: 'SpO2 (Intraday)', description: 'Minute-level blood oxygen samples', section: 'SpO2 & Breathing' },
  { value: 'breathing_rate_avg', label: 'Breathing Rate Average', description: 'Average breathing rate (breaths/min)', section: 'SpO2 & Breathing' },
  { value: 'breathing_rate_intraday', label: 'Breathing Rate (Intraday)', description: 'Minute-level breathing rate samples', section: 'SpO2 & Breathing' },
];

// HealthKit data types - Complete list of all available metrics
const HEALTHKIT_DATA_TYPES = [
  // ═══════════════════════════════════════════════════════════════
  // SECTION 1: ACTIVITY & FITNESS (33 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'steps_total', label: 'Steps (Daily Total)', description: 'Total steps per day', section: 'Activity & Fitness' },
  { value: 'steps_intraday', label: 'Steps (Intraday)', description: 'Intraday step samples', section: 'Activity & Fitness' },
  { value: 'distance_walking_running_total', label: 'Distance Walking/Running', description: 'Total distance walking or running (meters)', section: 'Activity & Fitness' },
  { value: 'distance_walking_running_intraday', label: 'Distance Walking/Running (Intraday)', description: 'Intraday walking/running distance samples', section: 'Activity & Fitness' },
  { value: 'distance_cycling_total', label: 'Distance Cycling', description: 'Total distance cycling (meters)', section: 'Activity & Fitness' },
  { value: 'distance_cycling_intraday', label: 'Distance Cycling (Intraday)', description: 'Intraday cycling distance samples', section: 'Activity & Fitness' },
  { value: 'distance_swimming_total', label: 'Distance Swimming', description: 'Total distance swimming (meters)', section: 'Activity & Fitness' },
  { value: 'distance_swimming_intraday', label: 'Distance Swimming (Intraday)', description: 'Intraday swimming distance samples', section: 'Activity & Fitness' },
  { value: 'distance_wheelchair_total', label: 'Distance Wheelchair', description: 'Total distance in wheelchair (meters)', section: 'Activity & Fitness' },
  { value: 'distance_wheelchair_intraday', label: 'Distance Wheelchair (Intraday)', description: 'Intraday wheelchair distance samples', section: 'Activity & Fitness' },
  { value: 'distance_downhill_snow_total', label: 'Distance Downhill Snow Sports', description: 'Total downhill snow sports distance (meters)', section: 'Activity & Fitness' },
  { value: 'distance_cross_country_skiing_total', label: 'Distance Cross-Country Skiing', description: 'Total cross-country skiing distance (meters)', section: 'Activity & Fitness' },
  { value: 'distance_paddle_sports_total', label: 'Distance Paddle Sports', description: 'Total paddle sports distance (meters)', section: 'Activity & Fitness' },
  { value: 'distance_rowing_total', label: 'Distance Rowing', description: 'Total rowing distance (meters)', section: 'Activity & Fitness' },
  { value: 'distance_skating_total', label: 'Distance Skating Sports', description: 'Total skating sports distance (meters)', section: 'Activity & Fitness' },
  { value: 'flights_climbed_total', label: 'Flights Climbed', description: 'Number of flights of stairs climbed', section: 'Activity & Fitness' },
  { value: 'flights_climbed_intraday', label: 'Flights Climbed (Intraday)', description: 'Intraday flights climbed samples', section: 'Activity & Fitness' },
  { value: 'active_energy_burned_total', label: 'Active Energy Burned', description: 'Total active calories burned (kcal)', section: 'Activity & Fitness' },
  { value: 'active_energy_burned_intraday', label: 'Active Energy Burned (Intraday)', description: 'Intraday active energy samples', section: 'Activity & Fitness' },
  { value: 'basal_energy_burned_total', label: 'Basal Energy Burned', description: 'Total basal (resting) calories burned (kcal)', section: 'Activity & Fitness' },
  { value: 'basal_energy_burned_intraday', label: 'Basal Energy Burned (Intraday)', description: 'Intraday basal energy samples', section: 'Activity & Fitness' },
  { value: 'exercise_time_total', label: 'Exercise Time', description: 'Total exercise time (minutes)', section: 'Activity & Fitness' },
  { value: 'exercise_time_intraday', label: 'Exercise Time (Intraday)', description: 'Intraday exercise time samples', section: 'Activity & Fitness' },
  { value: 'stand_time_total', label: 'Stand Time', description: 'Total stand time (minutes)', section: 'Activity & Fitness' },
  { value: 'stand_time_intraday', label: 'Stand Time (Intraday)', description: 'Intraday stand time samples', section: 'Activity & Fitness' },
  { value: 'move_time_total', label: 'Move Time', description: 'Total move time (minutes)', section: 'Activity & Fitness' },
  { value: 'swimming_stroke_count_total', label: 'Swimming Stroke Count', description: 'Total swimming strokes', section: 'Activity & Fitness' },
  { value: 'swimming_stroke_count_intraday', label: 'Swimming Stroke Count (Intraday)', description: 'Intraday swimming stroke samples', section: 'Activity & Fitness' },
  { value: 'push_count_total', label: 'Push Count (Wheelchair)', description: 'Total wheelchair push count', section: 'Activity & Fitness' },
  { value: 'physical_effort_avg', label: 'Physical Effort', description: 'Average physical effort (kcal/kg/hr)', section: 'Activity & Fitness' },
  { value: 'estimated_workout_effort', label: 'Estimated Workout Effort', description: 'Estimated workout effort score', section: 'Activity & Fitness' },
  { value: 'workout_effort_score', label: 'Workout Effort Score', description: 'Workout effort score', section: 'Activity & Fitness' },
  { value: 'underwater_depth_max', label: 'Underwater Depth (Max)', description: 'Maximum underwater depth (meters)', section: 'Activity & Fitness' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 2: RUNNING METRICS (5 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'running_power_avg', label: 'Running Power', description: 'Average running power (watts)', section: 'Running Metrics' },
  { value: 'running_speed_avg', label: 'Running Speed', description: 'Average running speed (m/s)', section: 'Running Metrics' },
  { value: 'running_ground_contact_time_avg', label: 'Ground Contact Time', description: 'Average ground contact time (ms)', section: 'Running Metrics' },
  { value: 'running_vertical_oscillation_avg', label: 'Vertical Oscillation', description: 'Average vertical oscillation (mm)', section: 'Running Metrics' },
  { value: 'running_stride_length_avg', label: 'Stride Length', description: 'Average running stride length (meters)', section: 'Running Metrics' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 3: CYCLING METRICS (4 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'cycling_cadence_avg', label: 'Cycling Cadence', description: 'Average cycling cadence (rpm)', section: 'Cycling Metrics' },
  { value: 'cycling_power_avg', label: 'Cycling Power', description: 'Average cycling power (watts)', section: 'Cycling Metrics' },
  { value: 'cycling_ftp', label: 'Functional Threshold Power', description: 'Cycling FTP (watts)', section: 'Cycling Metrics' },
  { value: 'cycling_speed_avg', label: 'Cycling Speed', description: 'Average cycling speed (m/s)', section: 'Cycling Metrics' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 4: SPORT SPEEDS (3 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'cross_country_skiing_speed_avg', label: 'Cross-Country Skiing Speed', description: 'Average cross-country skiing speed (m/s)', section: 'Sport Speeds' },
  { value: 'paddle_sports_speed_avg', label: 'Paddle Sports Speed', description: 'Average paddle sports speed (m/s)', section: 'Sport Speeds' },
  { value: 'rowing_speed_avg', label: 'Rowing Speed', description: 'Average rowing speed (m/s)', section: 'Sport Speeds' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 5: HEART (12 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'resting_heart_rate', label: 'Resting Heart Rate', description: 'Resting heart rate (bpm)', section: 'Heart' },
  { value: 'walking_heart_rate_avg', label: 'Walking Heart Rate Average', description: 'Average heart rate while walking (bpm)', section: 'Heart' },
  { value: 'heart_rate_intraday', label: 'Heart Rate Intraday', description: 'Detailed heart rate samples throughout the day', section: 'Heart' },
  { value: 'hrv_sdnn', label: 'Heart Rate Variability (SDNN)', description: 'HRV standard deviation (ms)', section: 'Heart' },
  { value: 'hrv_intraday', label: 'Heart Rate Variability (Intraday)', description: 'Intraday HRV samples', section: 'Heart' },
  { value: 'hrv_rmssd', label: 'HRV RMSSD (Beat-to-Beat)', description: 'Root mean square of successive RR interval differences (ms)', section: 'Heart' },
  { value: 'hrv_pnn50', label: 'HRV pNN50 (Beat-to-Beat)', description: 'Percentage of successive RR intervals differing by >50ms (%)', section: 'Heart' },
  { value: 'hrv_mean_rr', label: 'HRV Mean RR Interval', description: 'Mean RR interval from beat-to-beat data (ms)', section: 'Heart' },
  { value: 'hrv_beat_to_beat', label: 'HRV Raw Beat-to-Beat Intervals', description: 'Raw RR intervals with timestamps and gap flags (JSONB)', section: 'Heart' },
  { value: 'heart_rate_recovery', label: 'Heart Rate Recovery', description: 'Heart rate decrease after exercise (bpm)', section: 'Heart' },
  { value: 'afib_burden', label: 'Atrial Fibrillation Burden', description: 'AFib burden percentage (%)', section: 'Heart' },
  { value: 'peripheral_perfusion_index', label: 'Peripheral Perfusion Index', description: 'Peripheral perfusion index (%)', section: 'Heart' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 6: BODY MEASUREMENTS (8 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'body_mass', label: 'Body Mass (Weight)', description: 'Body weight (kg)', section: 'Body Measurements' },
  { value: 'height', label: 'Height', description: 'Height (cm)', section: 'Body Measurements' },
  { value: 'body_fat_percentage', label: 'Body Fat Percentage', description: 'Body fat percentage (%)', section: 'Body Measurements' },
  { value: 'bmi', label: 'Body Mass Index (BMI)', description: 'BMI (kg/m²)', section: 'Body Measurements' },
  { value: 'lean_body_mass', label: 'Lean Body Mass', description: 'Lean body mass (kg)', section: 'Body Measurements' },
  { value: 'waist_circumference', label: 'Waist Circumference', description: 'Waist circumference (cm)', section: 'Body Measurements' },
  { value: 'electrodermal_activity', label: 'Electrodermal Activity', description: 'Electrodermal activity (siemens)', section: 'Body Measurements' },
  { value: 'sleeping_wrist_temperature', label: 'Sleeping Wrist Temperature', description: 'Sleeping wrist temperature deviation (°C)', section: 'Body Measurements' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 7: SLEEP (4 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'sleep_duration', label: 'Sleep Duration', description: 'Total sleep time (minutes)', section: 'Sleep' },
  { value: 'sleep_stages', label: 'Sleep Stages', description: 'Sleep stages breakdown (REM, deep, core, awake)', section: 'Sleep' },
  { value: 'sleep_intraday', label: 'Sleep Intraday', description: 'Detailed sleep stage transitions', section: 'Sleep' },
  { value: 'time_in_bed', label: 'Time in Bed', description: 'Total time in bed (minutes)', section: 'Sleep' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 8: RESPIRATORY (8 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'respiratory_rate_avg', label: 'Respiratory Rate Average', description: 'Average breathing rate (breaths/min)', section: 'Respiratory' },
  { value: 'respiratory_rate_intraday', label: 'Respiratory Rate (Intraday)', description: 'Intraday respiratory rate samples', section: 'Respiratory' },
  { value: 'vo2_max', label: 'VO2 Max', description: 'Maximal oxygen consumption (mL/kg/min)', section: 'Respiratory' },
  { value: 'oxygen_saturation_avg', label: 'Oxygen Saturation Average', description: 'Average blood oxygen level (%)', section: 'Respiratory' },
  { value: 'oxygen_saturation_intraday', label: 'Oxygen Saturation (Intraday)', description: 'Intraday oxygen saturation samples', section: 'Respiratory' },
  { value: 'forced_expiratory_volume1', label: 'Forced Expiratory Volume (FEV1)', description: 'Forced expiratory volume in 1 second (liters)', section: 'Respiratory' },
  { value: 'forced_vital_capacity', label: 'Forced Vital Capacity', description: 'Forced vital capacity (liters)', section: 'Respiratory' },
  { value: 'peak_expiratory_flow_rate', label: 'Peak Expiratory Flow Rate', description: 'Peak expiratory flow rate (L/min)', section: 'Respiratory' },
  { value: 'inhaler_usage_total', label: 'Inhaler Usage', description: 'Total inhaler usage count', section: 'Respiratory' },
  { value: 'sleeping_breathing_disturbances', label: 'Sleeping Breathing Disturbances', description: 'Sleeping breathing disturbances count', section: 'Respiratory' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 9: VITALS (10 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'blood_pressure_systolic', label: 'Blood Pressure (Systolic)', description: 'Systolic blood pressure (mmHg)', section: 'Vitals' },
  { value: 'blood_pressure_diastolic', label: 'Blood Pressure (Diastolic)', description: 'Diastolic blood pressure (mmHg)', section: 'Vitals' },
  { value: 'blood_pressure_intraday', label: 'Blood Pressure (Intraday)', description: 'Intraday blood pressure samples', section: 'Vitals' },
  { value: 'body_temperature', label: 'Body Temperature', description: 'Body temperature (°C)', section: 'Vitals' },
  { value: 'body_temperature_intraday', label: 'Body Temperature (Intraday)', description: 'Intraday body temperature samples', section: 'Vitals' },
  { value: 'basal_body_temperature', label: 'Basal Body Temperature', description: 'Basal body temperature (°C)', section: 'Vitals' },
  { value: 'blood_glucose_avg', label: 'Blood Glucose Average', description: 'Average blood glucose (mg/dL)', section: 'Vitals' },
  { value: 'blood_glucose_intraday', label: 'Blood Glucose (Intraday)', description: 'Intraday blood glucose readings', section: 'Vitals' },
  { value: 'blood_alcohol_content', label: 'Blood Alcohol Content', description: 'Blood alcohol content (%)', section: 'Vitals' },
  { value: 'insulin_delivery_total', label: 'Insulin Delivery', description: 'Total insulin delivered (IU)', section: 'Vitals' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 10: NUTRITION - MACROS (14 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'dietary_energy_consumed', label: 'Dietary Energy (Calories)', description: 'Total calories consumed (kcal)', section: 'Nutrition - Macros' },
  { value: 'dietary_protein', label: 'Dietary Protein', description: 'Total protein consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_carbohydrates', label: 'Dietary Carbohydrates', description: 'Total carbohydrates consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_fat_total', label: 'Dietary Fat (Total)', description: 'Total fat consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_fat_saturated', label: 'Dietary Fat (Saturated)', description: 'Saturated fat consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_fat_monounsaturated', label: 'Dietary Fat (Monounsaturated)', description: 'Monounsaturated fat consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_fat_polyunsaturated', label: 'Dietary Fat (Polyunsaturated)', description: 'Polyunsaturated fat consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_cholesterol', label: 'Dietary Cholesterol', description: 'Total cholesterol consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_sugar', label: 'Dietary Sugar', description: 'Total sugar consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_fiber', label: 'Dietary Fiber', description: 'Total fiber consumed (g)', section: 'Nutrition - Macros' },
  { value: 'dietary_sodium', label: 'Dietary Sodium', description: 'Total sodium consumed (mg)', section: 'Nutrition - Macros' },
  { value: 'dietary_water', label: 'Dietary Water', description: 'Total water consumed (mL)', section: 'Nutrition - Macros' },
  { value: 'dietary_caffeine', label: 'Dietary Caffeine', description: 'Total caffeine consumed (mg)', section: 'Nutrition - Macros' },
  { value: 'food_log', label: 'Food Log', description: 'Structured food log entries (JSON)', section: 'Nutrition - Macros' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 11: NUTRITION - VITAMINS (13 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'dietary_vitamin_a', label: 'Vitamin A', description: 'Total Vitamin A consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_vitamin_b6', label: 'Vitamin B6', description: 'Total Vitamin B6 consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_vitamin_b12', label: 'Vitamin B12', description: 'Total Vitamin B12 consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_vitamin_c', label: 'Vitamin C', description: 'Total Vitamin C consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_vitamin_d', label: 'Vitamin D', description: 'Total Vitamin D consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_vitamin_e', label: 'Vitamin E', description: 'Total Vitamin E consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_vitamin_k', label: 'Vitamin K', description: 'Total Vitamin K consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_thiamin', label: 'Thiamin (B1)', description: 'Total Thiamin consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_riboflavin', label: 'Riboflavin (B2)', description: 'Total Riboflavin consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_niacin', label: 'Niacin (B3)', description: 'Total Niacin consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_folate', label: 'Folate (B9)', description: 'Total Folate consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_pantothenic_acid', label: 'Pantothenic Acid (B5)', description: 'Total Pantothenic Acid consumed (mg)', section: 'Nutrition - Vitamins' },
  { value: 'dietary_biotin', label: 'Biotin (B7)', description: 'Total Biotin consumed (mg)', section: 'Nutrition - Vitamins' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 12: NUTRITION - MINERALS (13 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'dietary_calcium', label: 'Calcium', description: 'Total Calcium consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_iron', label: 'Iron', description: 'Total Iron consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_magnesium', label: 'Magnesium', description: 'Total Magnesium consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_phosphorus', label: 'Phosphorus', description: 'Total Phosphorus consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_potassium', label: 'Potassium', description: 'Total Potassium consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_zinc', label: 'Zinc', description: 'Total Zinc consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_iodine', label: 'Iodine', description: 'Total Iodine consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_copper', label: 'Copper', description: 'Total Copper consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_manganese', label: 'Manganese', description: 'Total Manganese consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_chromium', label: 'Chromium', description: 'Total Chromium consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_molybdenum', label: 'Molybdenum', description: 'Total Molybdenum consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_selenium', label: 'Selenium', description: 'Total Selenium consumed (mg)', section: 'Nutrition - Minerals' },
  { value: 'dietary_chloride', label: 'Chloride', description: 'Total Chloride consumed (mg)', section: 'Nutrition - Minerals' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 13: MINDFULNESS (2 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'mindful_minutes_total', label: 'Mindful Minutes', description: 'Total mindfulness session time (minutes)', section: 'Mindfulness' },
  { value: 'mindful_sessions', label: 'Mindful Sessions', description: 'Detailed mindfulness session records', section: 'Mindfulness' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 14: MOBILITY (8 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'walking_speed_avg', label: 'Walking Speed Average', description: 'Average walking speed (m/s)', section: 'Mobility' },
  { value: 'walking_step_length', label: 'Walking Step Length', description: 'Average step length (cm)', section: 'Mobility' },
  { value: 'walking_asymmetry_percentage', label: 'Walking Asymmetry', description: 'Walking asymmetry percentage (%)', section: 'Mobility' },
  { value: 'walking_double_support_percentage', label: 'Walking Double Support', description: 'Double support time percentage (%)', section: 'Mobility' },
  { value: 'stair_ascent_speed', label: 'Stair Ascent Speed', description: 'Speed going up stairs (m/s)', section: 'Mobility' },
  { value: 'stair_descent_speed', label: 'Stair Descent Speed', description: 'Speed going down stairs (m/s)', section: 'Mobility' },
  { value: 'six_minute_walk_distance', label: 'Six Minute Walk Distance', description: 'Distance walked in 6 minutes (meters)', section: 'Mobility' },
  { value: 'walking_steadiness', label: 'Walking Steadiness', description: 'Walking steadiness score (%)', section: 'Mobility' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 15: HEARING (5 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'environmental_audio_exposure_avg', label: 'Environmental Audio Exposure', description: 'Average environmental noise level (dB)', section: 'Hearing' },
  { value: 'environmental_audio_exposure_intraday', label: 'Environmental Audio Exposure (Intraday)', description: 'Intraday environmental audio samples', section: 'Hearing' },
  { value: 'headphone_audio_exposure_avg', label: 'Headphone Audio Exposure', description: 'Average headphone audio level (dB)', section: 'Hearing' },
  { value: 'headphone_audio_exposure_intraday', label: 'Headphone Audio Exposure (Intraday)', description: 'Intraday headphone audio samples', section: 'Hearing' },
  { value: 'environmental_sound_reduction_avg', label: 'Environmental Sound Reduction', description: 'Environmental sound reduction (dB)', section: 'Hearing' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 16: ENVIRONMENT & OTHER (6 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'uv_exposure', label: 'UV Exposure', description: 'UV index exposure', section: 'Environment & Other' },
  { value: 'time_in_daylight', label: 'Time in Daylight', description: 'Time spent in daylight (minutes)', section: 'Environment & Other' },
  { value: 'water_temperature', label: 'Water Temperature', description: 'Water temperature (°C)', section: 'Environment & Other' },
  { value: 'times_fallen_total', label: 'Times Fallen', description: 'Total fall count', section: 'Environment & Other' },
  { value: 'alcoholic_beverages_total', label: 'Alcoholic Beverages', description: 'Total alcoholic beverages count', section: 'Environment & Other' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 17: SYMPTOMS (39 metrics)
  // Severity: 0=not present, 1=present, 2=mild, 3=moderate, 4=severe
  // mood_changes and sleep_changes: 0=not present, 1=present
  // ═══════════════════════════════════════════════════════════════
  { value: 'abdominal_cramps', label: 'Abdominal Cramps', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'acne', label: 'Acne', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'appetite_changes', label: 'Appetite Changes', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'bladder_incontinence', label: 'Bladder Incontinence', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'bloating', label: 'Bloating', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'breast_pain', label: 'Breast Pain', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'chest_tightness_or_pain', label: 'Chest Tightness or Pain', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'chills', label: 'Chills', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'constipation', label: 'Constipation', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'coughing', label: 'Coughing', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'diarrhea', label: 'Diarrhea', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'dizziness', label: 'Dizziness', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'dry_skin', label: 'Dry Skin', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'fainting', label: 'Fainting', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'fatigue', label: 'Fatigue', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'fever', label: 'Fever', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'generalized_body_ache', label: 'Generalized Body Ache', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'hair_loss', label: 'Hair Loss', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'headache', label: 'Headache', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'heartburn', label: 'Heartburn', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'hot_flashes', label: 'Hot Flashes', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'loss_of_smell', label: 'Loss of Smell', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'loss_of_taste', label: 'Loss of Taste', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'lower_back_pain', label: 'Lower Back Pain', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'memory_lapse', label: 'Memory Lapse', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'mood_changes', label: 'Mood Changes', description: 'Presence (0=not present, 1=present)', section: 'Symptoms' },
  { value: 'nausea', label: 'Nausea', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'night_sweats', label: 'Night Sweats', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'pelvic_pain', label: 'Pelvic Pain', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'rapid_pounding_or_fluttering_heartbeat', label: 'Rapid/Pounding Heartbeat', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'runny_nose', label: 'Runny Nose', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'shortness_of_breath', label: 'Shortness of Breath', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'sinus_congestion', label: 'Sinus Congestion', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'skipped_heartbeat', label: 'Skipped Heartbeat', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'sleep_changes', label: 'Sleep Changes', description: 'Presence (0=not present, 1=present)', section: 'Symptoms' },
  { value: 'sore_throat', label: 'Sore Throat', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'vaginal_dryness', label: 'Vaginal Dryness', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'vomiting', label: 'Vomiting', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },
  { value: 'wheezing', label: 'Wheezing', description: 'Max severity (0=none, 4=severe)', section: 'Symptoms' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 18: REPRODUCTIVE HEALTH (16 metrics)
  // Values are type-specific HealthKit enum integers
  // ═══════════════════════════════════════════════════════════════
  { value: 'cervical_mucus_quality', label: 'Cervical Mucus Quality', description: '1=dry, 2=sticky, 3=creamy, 4=watery, 5=egg white', section: 'Reproductive Health' },
  { value: 'intermenstrual_bleeding', label: 'Intermenstrual Bleeding', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'menstrual_flow', label: 'Menstrual Flow', description: '0=unspecified, 1=none, 2=light, 3=medium, 4=heavy', section: 'Reproductive Health' },
  { value: 'ovulation_test_result', label: 'Ovulation Test Result', description: '1=negative, 2=LH surge, 3=indeterminate, 4=estrogen surge', section: 'Reproductive Health' },
  { value: 'sexual_activity', label: 'Sexual Activity', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'contraceptive', label: 'Contraceptive', description: '1=unspecified, 2=implant, 3=injection, 4=IUD, 5=ring, 6=oral, 7=patch', section: 'Reproductive Health' },
  { value: 'lactation', label: 'Lactation', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'pregnancy', label: 'Pregnancy', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'pregnancy_test_result', label: 'Pregnancy Test Result', description: '1=negative, 2=positive, 3=indeterminate', section: 'Reproductive Health' },
  { value: 'progesterone_test_result', label: 'Progesterone Test Result', description: '1=negative, 2=indeterminate, 3=positive', section: 'Reproductive Health' },
  { value: 'infrequent_menstrual_cycles', label: 'Infrequent Menstrual Cycles', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'irregular_menstrual_cycles', label: 'Irregular Menstrual Cycles', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'persistent_intermenstrual_bleeding', label: 'Persistent Intermenstrual Bleeding', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'prolonged_menstrual_periods', label: 'Prolonged Menstrual Periods', description: 'Presence (0=absent, 1=present)', section: 'Reproductive Health' },
  { value: 'bleeding_after_pregnancy', label: 'Bleeding After Pregnancy', description: '0=unspecified, 1=light, 2=medium, 3=heavy', section: 'Reproductive Health' },
  { value: 'bleeding_during_pregnancy', label: 'Bleeding During Pregnancy', description: '0=unspecified, 1=light, 2=medium, 3=heavy', section: 'Reproductive Health' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 19: HEALTH EVENTS (11 metrics)
  // All values are daily event counts
  // ═══════════════════════════════════════════════════════════════
  { value: 'stand_hours_count', label: 'Stand Hours', description: 'Hours the user stood (0–24)', section: 'Health Events' },
  { value: 'irregular_heart_rhythm_events', label: 'Irregular Heart Rhythm Events', description: 'AFib detection events per day', section: 'Health Events' },
  { value: 'low_heart_rate_events', label: 'Low Heart Rate Events', description: 'Low HR notification events per day', section: 'Health Events' },
  { value: 'high_heart_rate_events', label: 'High Heart Rate Events', description: 'High HR notification events per day', section: 'Health Events' },
  { value: 'low_cardio_fitness_events', label: 'Low Cardio Fitness Events', description: 'Low VO2 max notification events per day', section: 'Health Events' },
  { value: 'environmental_audio_exposure_events', label: 'Environmental Audio Events', description: 'Loud environment threshold events per day', section: 'Health Events' },
  { value: 'headphone_audio_exposure_events', label: 'Headphone Audio Events', description: 'Loud headphone threshold events per day', section: 'Health Events' },
  { value: 'walking_steadiness_events', label: 'Walking Steadiness Events', description: 'Low/very low steadiness events per day', section: 'Health Events' },
  { value: 'sleep_apnea_events', label: 'Sleep Apnea Events', description: 'Sleep apnea detections per day', section: 'Health Events' },
  { value: 'handwashing_events', label: 'Handwashing Events', description: 'Handwashing sessions detected per day', section: 'Health Events' },
  { value: 'toothbrushing_events', label: 'Toothbrushing Events', description: 'Toothbrushing sessions detected per day', section: 'Health Events' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 20: STATE OF MIND (iOS 17+, 2 metrics)
  // Valence is -1.0 (very unpleasant) to +1.0 (very pleasant).
  // Intraday array preserves per-entry kind, labels, and associations.
  // ═══════════════════════════════════════════════════════════════
  { value: 'state_of_mind_valence_avg', label: 'State of Mind: Valence (Daily Avg)', description: 'Daily mean valence (-1.0 unpleasant ↔ +1.0 pleasant)', section: 'State of Mind' },
  { value: 'state_of_mind_intraday', label: 'State of Mind (Intraday)', description: 'Per-entry valence, kind, labels, and associations', section: 'State of Mind' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 21: WORKOUTS (3 metrics)
  // Per-workout activity type, duration, energy, and distance.
  // ═══════════════════════════════════════════════════════════════
  { value: 'workout_count', label: 'Workout Count', description: 'Number of workouts started this day', section: 'Workouts' },
  { value: 'workout_total_duration', label: 'Workout Total Duration', description: 'Sum of workout durations (seconds)', section: 'Workouts' },
  { value: 'workouts_intraday', label: 'Workouts (Intraday)', description: 'Per-workout array (activity, duration, energy, distance)', section: 'Workouts' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 22: ACTIVITY SUMMARY (6 metrics)
  // Daily move/exercise/stand ring totals plus their goals.
  // ═══════════════════════════════════════════════════════════════
  { value: 'activity_summary_active_energy', label: 'Activity: Active Energy', description: 'Daily active energy burned (kcal)', section: 'Activity Summary' },
  { value: 'activity_summary_active_energy_goal', label: 'Activity: Active Energy Goal', description: 'Daily active energy goal (kcal)', section: 'Activity Summary' },
  { value: 'activity_summary_exercise_time', label: 'Activity: Exercise Time', description: 'Daily exercise time (minutes)', section: 'Activity Summary' },
  { value: 'activity_summary_exercise_time_goal', label: 'Activity: Exercise Time Goal', description: 'Daily exercise time goal (minutes)', section: 'Activity Summary' },
  { value: 'activity_summary_stand_hours', label: 'Activity: Stand Hours', description: 'Daily stand hours (out of 24)', section: 'Activity Summary' },
  { value: 'activity_summary_stand_hours_goal', label: 'Activity: Stand Hours Goal', description: 'Daily stand hours goal', section: 'Activity Summary' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 23: ELECTROCARDIOGRAMS (2 metrics)
  // Per-reading summary; raw voltage stream NOT included.
  // ═══════════════════════════════════════════════════════════════
  { value: 'ecg_count', label: 'ECG Count', description: 'Number of ECG recordings this day', section: 'Electrocardiograms' },
  { value: 'ecg_intraday', label: 'ECG (Intraday)', description: 'Per-reading classification, avg HR, sampling freq', section: 'Electrocardiograms' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 24: AUDIOGRAMS (1 metric)
  // Hearing test results — sensitivity points per ear per frequency.
  // ═══════════════════════════════════════════════════════════════
  { value: 'audiograms_intraday', label: 'Audiograms (Intraday)', description: 'Hearing test sensitivity points (frequency + dB HL per ear)', section: 'Audiograms' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 25: SCORED ASSESSMENTS (2 metrics)
  // Self-administered mental-health screeners.
  // ═══════════════════════════════════════════════════════════════
  { value: 'gad7_intraday', label: 'GAD-7 (Anxiety Screener)', description: 'GAD-7 anxiety screener scores (0-21 per entry)', section: 'Scored Assessments' },
  { value: 'phq9_intraday', label: 'PHQ-9 (Depression Screener)', description: 'PHQ-9 depression screener scores (0-27 per entry)', section: 'Scored Assessments' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 26: METADATA (2 metrics)
  // ═══════════════════════════════════════════════════════════════
  { value: 'data_quality_score', label: 'Data Quality Score', description: 'Data completeness score (0.00 - 1.00)', section: 'Metadata' },
  { value: 'extraction_errors', label: 'Extraction Errors', description: 'Extraction error details (JSON)', section: 'Metadata' },

  // ═══════════════════════════════════════════════════════════════
  // SECTION 27: CLINICAL RECORDS / EHR (9 types)
  // From Apple Health Records (FHIR R4 format)
  // ═══════════════════════════════════════════════════════════════
  { value: 'AllergyRecord', label: 'Allergy Records', description: 'Allergy and intolerance records (FHIR AllergyIntolerance)', section: 'Clinical Records (EHR)' },
  { value: 'ClinicalNoteRecord', label: 'Clinical Notes', description: 'Clinical notes from providers (FHIR DiagnosticReport, DocumentReference)', section: 'Clinical Records (EHR)' },
  { value: 'ConditionRecord', label: 'Condition Records', description: 'Medical conditions and diagnoses (FHIR Condition)', section: 'Clinical Records (EHR)' },
  { value: 'ImmunizationRecord', label: 'Immunization Records', description: 'Vaccination and immunization records (FHIR Immunization)', section: 'Clinical Records (EHR)' },
  { value: 'LabResultRecord', label: 'Lab Result Records', description: 'Laboratory test results (FHIR Observation)', section: 'Clinical Records (EHR)' },
  { value: 'MedicationRecord', label: 'Medication Records', description: 'Medication prescriptions and orders (FHIR MedicationRequest)', section: 'Clinical Records (EHR)' },
  { value: 'ProcedureRecord', label: 'Procedure Records', description: 'Medical procedures performed (FHIR Procedure)', section: 'Clinical Records (EHR)' },
  { value: 'VitalSignRecord', label: 'Vital Sign Records', description: 'Clinical vital sign measurements (FHIR Observation)', section: 'Clinical Records (EHR)' },
  { value: 'CoverageRecord', label: 'Coverage Records', description: 'Insurance coverage information (FHIR Coverage)', section: 'Clinical Records (EHR)' },
];

// EHR record type values — used to route these to /api/ehr/extract instead of /api/healthkit/extract-data-from-db
const EHR_RECORD_TYPES = [
  'AllergyRecord', 'ClinicalNoteRecord', 'ConditionRecord', 'ImmunizationRecord',
  'LabResultRecord', 'MedicationRecord', 'ProcedureRecord', 'VitalSignRecord', 'CoverageRecord',
];

const DataExtractionInterface: React.FC<DataExtractionInterfaceProps> = ({
  studyId,
  studyName,
  participants,
  onRefreshParticipants,
  source
}) => {
  const [selectedDataSource, setDataSource] = useState<'fitbit' | 'healthkit'>(source || 'fitbit');
  const participantSelectId = React.useId();
  const dataSource = source || selectedDataSource;
  const { user } = useSecureAuth();
  const [dateRange, setDateRange] = useState<DateRangeSelection>({
    startDate: null,
    endDate: null,
    mode: 'single'
  });
  const [selectedDataTypes, setSelectedDataTypes] = useState<string[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  // Load extraction jobs from localStorage on mount (fallback)
  const loadExtractionJobsFromLocalStorage = useCallback((): DataExtractionJob[] => {
    try {
      const stored = localStorage.getItem(`extractionJobs_${studyId}`);
      if (stored) {
        const jobs = JSON.parse(stored);
        // Convert date strings back to Date objects
        return jobs.map((job: any) => ({
          ...job,
          createdAt: new Date(job.createdAt),
          completedAt: job.completedAt ? new Date(job.completedAt) : undefined,
          dateRange: {
            ...job.dateRange,
            startDate: job.dateRange.startDate ? new Date(job.dateRange.startDate) : null,
            endDate: job.dateRange.endDate ? new Date(job.dateRange.endDate) : null,
          },
        }));
      }
    } catch (error) {
      // Silent fail - fall back to empty array
    }
    return [];
  }, [studyId]);

  const [extractionJobs, setExtractionJobs] = useState<DataExtractionJob[]>(
    () => loadExtractionJobsFromLocalStorage()
  );
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [isRefreshingJobs, setIsRefreshingJobs] = useState(false);
  const [jobsRefreshError, setJobsRefreshError] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string>('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [extractionName, setExtractionName] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedJobForDetail, setSelectedJobForDetail] = useState<DataExtractionJob | null>(null);
  const [bucketName, setBucketName] = useState<string | null>(null);
  const [isLoadingBucketUrl, setIsLoadingBucketUrl] = useState(false);
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(false);
  const [jobsDialogOpen, setJobsDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<DataExtractionJob | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloadJob, setDownloadJob] = useState<DataExtractionJob | null>(null);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);

  // Progressive disclosure state
  const [showBucketHelp, setShowBucketHelp] = useState(
    () => !localStorage.getItem(`gcs_help_dismissed_${studyId}`)
  );
  const [showDateTips, setShowDateTips] = useState(false);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    () => new Set([source ? 'participants' : 'dataSource'])
  );

  // Restore config state from session on mount
  useEffect(() => {
    const session = loadDashboardSession(studyId);
    if (session?.extractionConfig) {
      const cfg = session.extractionConfig;
      // Do not restore Fitbit metric IDs or dates into a HealthKit export form.
      if (source && cfg.dataSource !== source) return;
      if (cfg.dataSource) setDataSource(cfg.dataSource as 'fitbit' | 'healthkit');
      if (cfg.dateRange) {
        setDateRange({
          startDate: cfg.dateRange.startDate ? new Date(cfg.dateRange.startDate) : null,
          endDate: cfg.dateRange.endDate ? new Date(cfg.dateRange.endDate) : null,
          mode: (cfg.dateRange.mode as 'single' | 'range') || 'single',
        });
      }
      if (cfg.selectedParticipants) {
        setSelectedParticipants(Array.from(new Set<string>(cfg.selectedParticipants)));
      }
      if (cfg.selectedDataTypes) setSelectedDataTypes(cfg.selectedDataTypes);
      if (cfg.expandedAccordions) setExpandedAccordions(new Set(cfg.expandedAccordions));
    }
  }, [studyId, source]);

  // Save config state to session whenever it changes
  useEffect(() => {
    saveDashboardSession(studyId, {
      extractionConfig: {
        dataSource,
        dateRange: {
          startDate: dateRange.startDate?.toISOString(),
          endDate: dateRange.endDate?.toISOString(),
          mode: dateRange.mode,
        },
        selectedParticipants,
        selectedDataTypes,
        expandedAccordions: Array.from(expandedAccordions),
      },
    });
  }, [studyId, dataSource, dateRange, selectedParticipants, selectedDataTypes, expandedAccordions]);

  const toggleAccordion = (panel: string) => {
    setExpandedAccordions(prev => {
      const next = new Set(prev);
      if (next.has(panel)) next.delete(panel);
      else next.add(panel);
      return next;
    });
  };

  const loadJobsFromFirestore = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshingJobs(true);
      setJobsRefreshError('');
    } else {
      setIsLoadingJobs(true);
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        if (isManualRefresh) {
          setJobsRefreshError('Please sign in to refresh extraction jobs.');
        }
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/fitbit/extraction-jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || `Unable to refresh extraction jobs (${response.status})`);
      }
      if (!data?.success || !Array.isArray(data.jobs?.all)) {
        throw new Error(data?.error || 'Extraction jobs response was invalid.');
      }

      // Convert Firestore jobs to frontend format
      const firestoreJobs: DataExtractionJob[] = data.jobs.all.map((job: any) => ({
        id: job.jobId || job.id,
        jobId: job.jobId,
        clientRequestId: job.clientRequestId,
        participantId: job.participantId,
        extractionName: job.extractionName || `Extraction_${job.jobId}`,
        dataTypes: job.dataTypes || [],
        dateRange: job.dateRange ? {
          startDate: job.dateRange.startDate ? new Date(job.dateRange.startDate) : null,
          endDate: job.dateRange.endDate ? new Date(job.dateRange.endDate) : null,
          mode: job.dateRange.mode || 'range'
        } : { startDate: null, endDate: null, mode: 'range' },
        status: getFitbitExtractionStatusSemantics(job.status).uiStatus,
        progress: job.progress || 0,
        backendStatus: typeof job.status === 'string' ? job.status : undefined,
        createdAt: job.createdAt ? new Date(job.createdAt) : new Date(),
        completedAt: getFitbitExtractionStatusSemantics(job.status).isTerminal && job.updatedAt
          ? new Date(job.updatedAt)
          : undefined,
        error: job.error || undefined,
        progressMessage: job.progressMessage,
        currentDate: job.currentDate,
        currentDataDate: job.currentDataDate,
        processedDates: typeof job.processedDates === 'number' ? job.processedDates : undefined,
        totalDates: typeof job.totalDates === 'number' ? job.totalDates : undefined,
        fileInfo: job.fileInfo || undefined
      }));

      // Merge with localStorage jobs (prefer Firestore, avoid duplicates)
      const localJobs = loadExtractionJobsFromLocalStorage();
      const firestoreJobIds = new Set(firestoreJobs.map(j => j.id));
      const SAME_JOB_WINDOW_MS = 10 * 60 * 1000; // 10 min

      const isSameLogicalJob = (local: DataExtractionJob, fs: DataExtractionJob) => {
        if (local.participantId !== fs.participantId) return false;
        if ((local.extractionName || '') !== (fs.extractionName || '')) return false;
        const localCreated = local.createdAt ? new Date(local.createdAt).getTime() : 0;
        const fsCreated = fs.createdAt ? new Date(fs.createdAt).getTime() : 0;
        return Math.abs(localCreated - fsCreated) < SAME_JOB_WINDOW_MS;
      };

      // Add local job only if not already in Firestore (by id, jobId, or same logical job)
      const mergedJobs = [...firestoreJobs];
      localJobs.forEach(localJob => {
        if (firestoreJobIds.has(localJob.id)) return;
        if (localJob.jobId && firestoreJobIds.has(localJob.jobId)) return;
        const alreadyInFirestore = firestoreJobs.some(fs => isSameLogicalJob(localJob, fs));
        if (alreadyInFirestore) return;
        mergedJobs.push(localJob);
      });

      setExtractionJobs(mergedJobs);

      try {
        localStorage.setItem(`extractionJobs_${studyId}`, JSON.stringify(mergedJobs));
      } catch (storageErr) {
        // Silent fail
      }
    } catch (caughtError) {
      // Initial load keeps the existing localStorage fallback. Manual refresh
      // keeps current rows visible and reports the failure inside this panel.
      if (isManualRefresh) {
        setJobsRefreshError(
          caughtError instanceof Error ? caughtError.message : 'Unable to refresh extraction jobs.'
        );
      }
    } finally {
      if (isManualRefresh) {
        setIsRefreshingJobs(false);
      } else {
        setIsLoadingJobs(false);
      }
    }
  }, [loadExtractionJobsFromLocalStorage, studyId]);

  // Load extraction jobs from Firestore on mount/study change.
  useEffect(() => {
    void loadJobsFromFirestore(false);
  }, [loadJobsFromFirestore]);

  // Save extraction jobs to localStorage whenever they change (as backup)
  useEffect(() => {
    try {
      localStorage.setItem(`extractionJobs_${studyId}`, JSON.stringify(extractionJobs));
    } catch (error) {
      // Silent fail
    }
  }, [extractionJobs, studyId]);

  const dataTypes = dataSource === 'fitbit' ? FITBIT_DATA_TYPES : HEALTHKIT_DATA_TYPES;
  const dataTypeValues = useMemo(() => dataTypes.map(dataType => dataType.value), [dataTypes]);
  const allDataTypesSelected = dataTypeValues.length > 0 && selectedDataTypes.length === dataTypeValues.length;

  const deviceMatchedParticipants = useMemo(
    // Inclusive membership: a "both" participant contributes Fitbit AND
    // HealthKit, so they must appear under either single-source extraction.
    () =>
      participants.filter((p) => {
        // Fresh Fitbit collection is automatic and does not use the retired
        // manual `/api/fitbit/*` extraction/export surface.
        if (dataSource === 'fitbit' && p.fitbitCollectionEnrollment === true) return false;
        return deviceTypeMatchesSource(getParticipantDeviceSource(p), dataSource);
      }),
    [participants, dataSource]
  );
  const authorizedParticipants = useMemo(
    () => deviceMatchedParticipants.filter((p) => p.authorizationStatus),
    [deviceMatchedParticipants]
  );
  const presetScopedParticipants = useMemo(() => {
    if (selectedParticipants.length === 0) {
      return authorizedParticipants;
    }

    const selectedIds = new Set(selectedParticipants);
    return authorizedParticipants.filter((participant) =>
      selectedIds.has(participant.participantId)
    );
  }, [authorizedParticipants, selectedParticipants]);

  const selectedParticipantAvailabilityRange = useMemo(() => {
    if (presetScopedParticipants.length === 0) {
      return {
        startDate: null as string | null,
        endDate: null as string | null,
      };
    }

    const availabilityWindows = presetScopedParticipants.map((participant) =>
      deriveParticipantHistoricalAvailability(participant)
    );
    const startDates = availabilityWindows
      .map((window) => window.startDate)
      .filter(Boolean)
      .sort();
    const endDates = availabilityWindows
      .map((window) => window.endDate)
      .filter(Boolean)
      .sort();

    return {
      startDate: startDates[0] ?? null,
      endDate: endDates[endDates.length - 1] ?? null,
    };
  }, [presetScopedParticipants]);

  // Calculate date restrictions based on participant enrollment dates
  const getDateRestrictions = () => {
    if (authorizedParticipants.length === 0) {
      return { minDate: null, maxDate: null, earliestEnrollment: null };
    }

    // Use the same UTC day for the date inputs, availability and validation.
    // Local end-of-day can become the following date when serialized to UTC.
    const maxDate = toUtcDate(getLatestExtractableDate());
    // Find the earliest enrollment date among selected participants
    const enrollmentDates = authorizedParticipants
      .filter(p => p.enrolledAt)
      .map(p => new Date(p.enrolledAt!))
      .filter(date => !isNaN(date.getTime()));

    if (enrollmentDates.length === 0) {
      return { minDate: null, maxDate, earliestEnrollment: null };
    }

    const earliestEnrollment = new Date(Math.min(...enrollmentDates.map(d => d.getTime())));

    // No minimum date - researchers can request data from any date in the past
    const minDate = null;

    return { minDate, maxDate, earliestEnrollment };
  };

  const dateRestrictions = getDateRestrictions();

  // Set bucket name when component mounts
  React.useEffect(() => {
    // Sanitize studyId for bucket name (lowercase, alphanumeric and hyphens only)
    const sanitizedStudyId = studyId.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    setBucketName(`study-${sanitizedStudyId}-data`);
  }, [studyId]);

  const handleDataTypeChange = (event: any) => {
    const value = event.target.value;
    setSelectedDataTypes(typeof value === 'string' ? value.split(',') : value);
  };

  const handleToggleAllDataTypes = () => {
    setSelectedDataTypes(allDataTypesSelected ? [] : dataTypeValues);
  };

  const chooseParticipants = (nextSelectedParticipants: string[]) => {
    setSelectedParticipants(nextSelectedParticipants);
    if (nextSelectedParticipants.length > 0) {
      setExpandedAccordions(prev => {
        const next = new Set(prev);
        next.add('dateRange');
        return next;
      });
    }
  };

  const clearParticipantSelection = () => {
    setSelectedParticipants([]);
  };

  const handleParticipantChange = (event: any) => {
    const value = event.target.value;
    chooseParticipants(Array.isArray(value) ? value : value ? value.split(',') : []);
  };

  useEffect(() => {
    if (selectedParticipants.length === 0) return;
    const allowedIds = new Set(authorizedParticipants.map((p) => p.participantId));
    if (selectedParticipants.some(id => !allowedIds.has(id))) setSelectedParticipants(current => current.filter(id => allowedIds.has(id)));
  }, [authorizedParticipants, selectedParticipants]);

  useEffect(() => {
    if (selectedParticipants.length === 0) {
      return;
    }

    const allowedStart = selectedParticipantAvailabilityRange.startDate;
    const allowedEnd = selectedParticipantAvailabilityRange.endDate;

    if (!allowedStart || !allowedEnd) {
      return;
    }

    const clampDateString = (dateStr: string): string => {
      if (dateStr < allowedStart) return allowedStart;
      if (dateStr > allowedEnd) return allowedEnd;
      return dateStr;
    };

    // Reconcile when participant availability changes, without undoing a
    // cleared or manually edited range while the researcher is choosing dates.
    setDateRange(previous => {
      const currentStart = previous.startDate ? toDateInputValue(previous.startDate) : null;
      const currentEnd = previous.endDate ? toDateInputValue(previous.endDate) : null;
      const nextStart = currentStart ? clampDateString(currentStart) : null;
      const nextEnd = currentEnd ? clampDateString(currentEnd) : null;
      return currentStart === nextStart && currentEnd === nextEnd ? previous : {
        ...previous, startDate: nextStart ? toUtcDate(nextStart) : null, endDate: nextEnd ? toUtcDate(nextEnd) : null,
      };
    });
  }, [selectedParticipantAvailabilityRange.endDate, selectedParticipantAvailabilityRange.startDate, selectedParticipants.length]);

  const validateExtractionRequest = (): string => {
    if (selectedParticipants.length === 0) {
      return 'Please select at least one participant';
    }

    if (selectedDataTypes.length === 0) {
      return 'Please select at least one data type';
    }

    const { startDate, endDate, mode } = dateRange;

    if (mode === 'single' && !startDate) {
      return 'Please select a date';
    }

    if (mode === 'range' && (!startDate || !endDate)) {
      return 'Please select both start and end dates';
    }

    if (!validRange({ startDate: startDate ? toDateInputValue(startDate) : '',
      endDate: mode === 'single' ? startDate ? toDateInputValue(startDate) : '' : endDate ? toDateInputValue(endDate) : '' })) {
      return 'Choose an ordered date range of up to 10 years';
    }

    // Validate date restrictions (only maxDate - no minimum limit)
    if (dateRestrictions.maxDate) {
      // Normalize dates to midnight for comparison (use UTC to avoid timezone issues)
      const normalizeDate = (date: Date) => {
        // Create a new date using UTC year, month, and day
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth();
        const day = date.getUTCDate();
        return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      };

      if (startDate) {
        const normalizedStartDate = normalizeDate(startDate);
        const normalizedMaxDate = normalizeDate(dateRestrictions.maxDate);

        // Only check that start date is not after maxDate (no minimum limit)
        if (normalizedStartDate.getTime() > normalizedMaxDate.getTime()) {
          return `Start date must be on or before ${dateRestrictions.maxDate.toISOString().split('T')[0]} (yesterday)`;
        }
      }

      if (endDate) {
        const normalizedEndDate = normalizeDate(endDate);
        const normalizedMaxDate = normalizeDate(dateRestrictions.maxDate);

        // Only check that end date is not after maxDate (no minimum limit)
        if (normalizedEndDate.getTime() > normalizedMaxDate.getTime()) {
          return `End date must be on or before ${dateRestrictions.maxDate.toISOString().split('T')[0]} (yesterday)`;
        }
      }

      if (startDate && endDate && startDate > endDate) {
        return 'Start date must be before or equal to end date';
      }
    }

    return '';
  };

  const handleStartExtraction = () => {
    const validationError = validateExtractionRequest();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Generate default extraction name
    const today = new Date().toISOString().split('T')[0];
    const participantSuffix = selectedParticipants.length === 1 ? 'participant' : 'participants';
    const defaultName = `Extraction_${today}_${selectedParticipants.length}${participantSuffix}`;
    setExtractionName(defaultName);
    setNameDialogOpen(true);
  };

  const handleConfirmExtraction = async () => {
    setNameDialogOpen(false);
    setIsExtracting(true);
    setError('');

    // Track extraction started
    analyticsService.trackExtractionStarted({
      studyId: studyId || '',
      participantCount: selectedParticipants.length,
      dataSource,
      dataTypes: selectedDataTypes,
      dateRangeMode: dateRange.mode
    });

    try {
      // Create extraction jobs for each participant
      const newJobs: DataExtractionJob[] = selectedParticipants.map(participantId => {
        const localJobId = `job_${Date.now()}_${participantId}`;
        const clientRequestId = dataSource === 'fitbit'
          ? `fitbit_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`
          : undefined;
        return {
          id: clientRequestId || localJobId,
          clientRequestId,
          participantId,
          extractionName,
          dataTypes: selectedDataTypes,
          dateRange,
          status: 'pending' as const,
          progress: 0,
          backendStatus: dataSource === 'fitbit' ? 'starting' : undefined,
          createdAt: new Date()
        };
      });

      setExtractionJobs(prev => [...prev, ...newJobs]);

      // Process each job
      for (const job of newJobs) {
        try {
          // Update job status to running
          setExtractionJobs(prev => prev.map(j => 
            j.id === job.id ? { ...j, status: 'running' as const } : j
          ));

          // Get authentication token
          const token = await apiService.getAuthToken();
          if (!token) {
            throw new Error('Authentication required');
          }

          // Split selected data types: EHR clinical record types route to /api/ehr/extract,
          // everything else routes to the source-specific endpoint
          const ehrTypes = dataSource === 'healthkit'
            ? job.dataTypes.filter((t: string) => EHR_RECORD_TYPES.includes(t))
            : [];
          const hkTypes = dataSource === 'healthkit'
            ? job.dataTypes.filter((t: string) => !EHR_RECORD_TYPES.includes(t))
            : job.dataTypes;

          const endpoint = dataSource === 'fitbit'
            ? 'fitbit/extract-data-from-db'
            : 'healthkit/extract-data-from-db';

          const startDateStr = dateRange.startDate?.toISOString().split('T')[0];
          const endDateStr = dateRange.mode === 'range' ? dateRange.endDate?.toISOString().split('T')[0] : dateRange.startDate?.toISOString().split('T')[0];

          // Call HealthKit/Fitbit endpoint (if any non-EHR types selected).
          // Each response may include `failed_types` (per-type serialization
          // errors). Aggregate across the HK and EHR calls so the job row
          // shows a single warning surface to the researcher.
          const aggregatedFailedTypes: FailedTypeEntry[] = [];
          let aggregatedTotalRecords = 0;
          const collectWarnings = (result: Record<string, unknown> | null) => {
            if (!result) return;
            const ft = result.failed_types as FailedTypeEntry[] | undefined;
            if (Array.isArray(ft)) {
              for (const entry of ft) {
                if (entry && typeof entry.type === 'string') {
                  aggregatedFailedTypes.push({ type: entry.type, reason: entry.reason });
                }
              }
            }
            const data = result.data as { summary?: { totalRecords?: number } } | undefined;
            const total = data?.summary?.totalRecords;
            if (typeof total === 'number') aggregatedTotalRecords += total;
          };
          let extractionResult: Record<string, unknown> | null = null;
          if (hkTypes.length > 0) {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/${endpoint}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                participantId: job.participantId,
                startDate: startDateStr,
                endDate: endDateStr,
                dataTypes: hkTypes,
                extractionName: job.extractionName,
                notifyOnCompletion: notifyOnCompletion,
                ...(job.clientRequestId ? { clientRequestId: job.clientRequestId } : {})
              }),
            });
            extractionResult = await response.json() as Record<string, unknown>;

            if (!response.ok || extractionResult.success === false) {
              const errorMessage = (extractionResult.error as string) || `HTTP ${response.status}: ${response.statusText}`;
              setExtractionJobs(prev => prev.map(j =>
                j.id === job.id
                  ? { ...j, status: 'failed' as const, error: errorMessage, completedAt: new Date() }
                  : j
              ));
              continue;
            }
            collectWarnings(extractionResult);
          }

          // Call EHR endpoint (if any clinical record types selected)
          if (ehrTypes.length > 0) {
            const ehrResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/ehr/extract`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                participantId: job.participantId,
                startDate: startDateStr,
                endDate: endDateStr,
                recordTypes: ehrTypes,
                extractionName: job.extractionName,
              }),
            });
            const ehrResult = await ehrResponse.json() as Record<string, unknown>;

            if (!ehrResponse.ok || ehrResult.success === false) {
              const errorMessage = (ehrResult.error as string) || `EHR extraction failed: HTTP ${ehrResponse.status}`;
              setExtractionJobs(prev => prev.map(j =>
                j.id === job.id
                  ? { ...j, status: 'failed' as const, error: errorMessage, completedAt: new Date() }
                  : j
              ));
              continue;
            }
            collectWarnings(ehrResult);

            // Use EHR result as primary if no HK types were requested
            if (!extractionResult) {
              extractionResult = ehrResult;
            }
          }

          // Fallback if somehow neither was called
          if (!extractionResult) {
            setExtractionJobs(prev => prev.map(j =>
              j.id === job.id
                ? { ...j, status: 'failed' as const, error: 'No data types selected', completedAt: new Date() }
                : j
            ));
            continue;
          }

          // If jobId is present, start polling for progress (works for both endpoints)
          if (extractionResult.jobId) {
            const backendJobId = extractionResult.jobId as string;
            // Update job with backend jobId and use it as id so we don't get duplicate rows when merging with Firestore
            setExtractionJobs(prev => prev.map(j => 
              j.id === job.id 
                ? { ...j, id: backendJobId, jobId: backendJobId }
                : j
            ));

            // Determine progress endpoint based on data source
            const progressEndpoint = dataSource === 'fitbit'
              ? `fitbit/extraction-progress/${backendJobId}`
              : `data-extraction/progress/${backendJobId}`;

            // Per-job polling: first poll at 5s, second at 20s, then every 10 min.
            const FIRST_POLL_DELAY_MS = 5 * 1000; // 5 seconds
            const SECOND_POLL_DELAY_MS = 20 * 1000; // 20 seconds
            const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
            const DAYS_PER_HOUR = 30; // historical pipeline extracts ~30 days per hour
            const GRACE_HOURS = 3;

            const start = job.dateRange?.startDate;
            const end = job.dateRange?.mode === 'range' && job.dateRange?.endDate
              ? job.dateRange.endDate
              : job.dateRange?.startDate;
            const oneDayMs = 24 * 60 * 60 * 1000;
            const daysInRange = start && end
              ? Math.max(1, Math.ceil((end.getTime() - start.getTime()) / oneDayMs) + 1)
              : 1;
            const hoursForBackfill = daysInRange / DAYS_PER_HOUR;
            const totalHours = hoursForBackfill + GRACE_HOURS;
            const pollsPerHour = 60 / 10; // 6 polls per hour at 10 min interval
            const maxAttempts = Math.max(6, Math.ceil(totalHours * pollsPerHour));

            // Poll for progress (capture for timeout message inside setInterval closure)
            const timeoutTotalHours = totalHours;
            const timeoutDaysInRange = daysInRange;
            let pollInterval: ReturnType<typeof setInterval> | null = null;

            const doOnePoll = async (attempt: number) => {
              try {
                const progressResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/${progressEndpoint}`,
                  {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  }
                );

                if (progressResponse.ok) {
                  const progressData = await progressResponse.json();

                  if (progressData.success && progressData.progress) {
                    const progress = progressData.progress;
                    const status = progress.status;
                    const percentage = progress.percentage ?? 0;
                    const message = progress.message || '';
                    const statusSemantics = getFitbitExtractionStatusSemantics(status);

                    // Update job with progress (match by backend job id)
                    setExtractionJobs(prev => prev.map(j =>
                      j.id === backendJobId
                        ? {
                            ...j,
                            progress: percentage,
                            backendStatus: typeof status === 'string' ? status : j.backendStatus,
                            progressMessage: message,
                            currentDate: progress.currentDate,
                            currentDataDate: progress.currentDataDate,
                            processedDates: typeof progress.processedDates === 'number'
                              ? progress.processedDates
                              : j.processedDates,
                            totalDates: typeof progress.totalDates === 'number'
                              ? progress.totalDates
                              : j.totalDates,
                            status: statusSemantics.uiStatus,
                            ...(statusSemantics.isTerminal ? {
                              completedAt: new Date(),
                              error: statusSemantics.isSuccessful
                                ? undefined
                                : message || (
                                  status === 'rate_limited'
                                    ? 'Extraction rate limited'
                                    : status === 'cancelled'
                                      ? 'Extraction cancelled'
                                      : 'Extraction failed'
                                )
                            } : {})
                          }
                        : j
                    ));

                    if (statusSemantics.isTerminal) {
                      if (pollInterval) clearInterval(pollInterval);
                      pollInterval = null;
                      return true; // signal to stop polling
                    }
                  }
                }
                return false;
              } catch {
                return false;
              }
            };

            const pollProgress = async () => {
              let attempts = 0;
              // First poll at 5 seconds
              setTimeout(async () => {
                attempts++;
                const stop = await doOnePoll(attempts);
                if (stop) return;
                // Second poll at 20 seconds (from start)
                setTimeout(async () => {
                  attempts++;
                  const stop2 = await doOnePoll(attempts);
                  if (stop2) return;
                  // Then every 10 min for long-running jobs
                  pollInterval = setInterval(async () => {
                    attempts++;
                    const stopN = await doOnePoll(attempts);
                    if (stopN && pollInterval) {
                      clearInterval(pollInterval);
                      pollInterval = null;
                      return;
                    }
                    if (attempts >= maxAttempts) {
                      if (pollInterval) clearInterval(pollInterval);
                      pollInterval = null;
                      setExtractionJobs(prev => prev.map(j =>
                        j.id === backendJobId
                          ? {
                              ...j,
                              status: 'failed' as const,
                              error: `Progress polling timeout after ${timeoutTotalHours.toFixed(1)} hours (${timeoutDaysInRange} days in range + 3h grace). The extraction may still be running on the server. Check the study bucket or try again later.`,
                              completedAt: new Date()
                            }
                          : j
                      ));
                    }
                  }, POLL_INTERVAL_MS);
                }, SECOND_POLL_DELAY_MS - FIRST_POLL_DELAY_MS); // 20s - 5s = 15s after first poll
              }, FIRST_POLL_DELAY_MS);
            };

            // Start polling: first at 5s, second at 20s, then every 10 min
            pollProgress();
          } else {
            // For non-API extraction or if no jobId, mark as completed immediately.
            // Stamp aggregated failed_types onto the job so the row banner
            // reflects the partial-success state.
            setExtractionJobs(prev => prev.map(j =>
              j.id === job.id
                  ? {
                    ...j,
                    status: 'completed' as const,
                    progress: 100,
                    completedAt: new Date(),
                    failedTypes: aggregatedFailedTypes.length > 0
                      ? aggregatedFailedTypes.slice()
                      : undefined,
                    totalRecords: aggregatedTotalRecords
                  }
                : j
            ));
          }

        } catch (error: any) {
          setExtractionJobs(prev => prev.map(j =>
            j.id === job.id 
              ? { 
                  ...j, 
                  status: 'failed' as const, 
                  error: error.message || 'Network error or request failed',
                  completedAt: new Date()
                }
              : j
          ));
        }
      }

    } catch (error: any) {
      setError(`Failed to start extraction: ${error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };

  const getJobStatusIcon = (status: DataExtractionJob['status']) => {
    switch (status) {
      case 'pending':
        return <Schedule color="action" />;
      case 'running':
        return <CircularProgress size={20} />;
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <Schedule color="action" />;
    }
  };

  const getJobStatusColor = (status: DataExtractionJob['status']) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'running':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleShowJobDetail = (job: DataExtractionJob) => {
    setSelectedJobForDetail(job);
    setDetailDialogOpen(true);
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>, job: DataExtractionJob) => {
    event.stopPropagation();
    setDownloadMenuAnchor(event.currentTarget);
    setDownloadJob(job);
  };
  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setDownloadJob(null);
  };
  const handleDownload = async (format: 'json' | 'zip') => {
    const selectedJob = downloadJob;
    if (!selectedJob?.jobId || downloadingJobId) return;

    handleDownloadMenuClose();
    setDownloadingJobId(selectedJob.jobId);

    try {
      setError('');
      const token = await apiService.getAuthToken();
      if (!token) {
        throw new Error('Please sign in to download.');
      }

      const url = `${process.env.REACT_APP_API_URL}/api/fitbit/extraction-jobs/${selectedJob.jobId}/download?format=${format}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="?([^";\n]+)"?/);
      const suggestedName = match?.[1] || (format === 'zip'
        ? `${selectedJob.extractionName || 'extraction'}.zip`
        : `${selectedJob.extractionName || 'extraction'}.json`);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e: any) {
      setError(e?.message || 'Download failed');
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDeleteJob = async (job: DataExtractionJob) => {
    setJobToDelete(job);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteJob = async () => {
    if (!jobToDelete) {
      setDeleteConfirmOpen(false);
      return;
    }

    const idToRemove = jobToDelete.id;
    let jobIdToRemove: string | null | undefined = jobToDelete.jobId;

    setIsDeleting(true);
    setError('');
    try {
      let token: string | null = null;
      if (jobIdToRemove || jobToDelete.clientRequestId) {
        token = await apiService.getAuthToken();
        if (!token) {
          throw new Error('Please sign in to delete this extraction job.');
        }
      }

      // A synchronous Fitbit request has not returned its backend jobId yet.
      // Resolve the durable job by its exact correlation ID instead of silently
      // deleting only the optimistic browser row.
      if (!jobIdToRemove && jobToDelete.clientRequestId && token) {
        jobIdToRemove = await waitForFitbitExtractionJobId({
          clientRequestId: jobToDelete.clientRequestId,
          loadJobs: async () => {
            const jobsResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/api/fitbit/extraction-jobs`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const jobsResult = await jobsResponse.json().catch(() => null);
            if (!jobsResponse.ok || !jobsResult?.success || !Array.isArray(jobsResult.jobs?.all)) {
              throw new Error(
                jobsResult?.error || `Unable to locate the running extraction (${jobsResponse.status})`
              );
            }
            return jobsResult.jobs.all;
          },
        });

        if (!jobIdToRemove) {
          throw new Error(
            'The extraction is still being registered. It was not removed; please try Delete again.'
          );
        }
      }

      // Delete on backend so Firestore is updated. Frontend uses REACT_APP_API_URL — if dev and prod
      // use different backends (and different Firestores), delete only affects the current environment.
      if (jobIdToRemove) {
        if (!token) {
          throw new Error('Please sign in to delete this extraction job.');
        }

        const deleteUrl = `${process.env.REACT_APP_API_URL}/api/fitbit/extraction-jobs/${jobIdToRemove}`;
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const deleteResult = await response.json().catch(() => null) as {
          success?: boolean;
          error?: string;
          message?: string;
        } | null;

        if (!shouldRemoveFitbitExtractionJobLocally({
          ok: response.ok,
          status: response.status,
          success: deleteResult?.success,
        })) {
          throw new Error(
            deleteResult?.error ||
            deleteResult?.message ||
            `Failed to delete extraction job (${response.status})`
          );
        }
      }

      // Remove locally only after the authoritative backend confirms deletion
      // (or reports it already absent with 404). Uncorrelated legacy jobs remain local-only.
      setExtractionJobs(prev => {
        const next = prev.filter(j =>
          j.id !== idToRemove &&
          j.jobId !== jobIdToRemove &&
          j.clientRequestId !== jobToDelete.clientRequestId
        );
        try {
          localStorage.setItem(`extractionJobs_${studyId}`, JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });

      setDeleteConfirmOpen(false);
      setJobToDelete(null);
      if (selectedJobForDetail?.id === jobToDelete.id) {
        setDetailDialogOpen(false);
        setSelectedJobForDetail(null);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to delete extraction job');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDateRange = (dateRange: DateRangeSelection) => {
    if (dateRange.mode === 'single' && dateRange.startDate) {
      return dateRange.startDate.toLocaleDateString();
    } else if (dateRange.mode === 'range' && dateRange.startDate && dateRange.endDate) {
      return `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`;
    }
    return 'No date selected';
  };

  const handleGoToBucket = async () => {
    if (!studyId) return;

    setIsLoadingBucketUrl(true);
    setError(''); // Clear any previous errors

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/studies/${studyId}/bucket-signed-url`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success && data.signedUrl) {
        window.open(data.signedUrl, '_blank');
      } else {
        setError(data.message || 'Failed to generate bucket URL');
      }
    } catch (error: any) {
      setError('Failed to access bucket: ' + error.message);
    } finally {
      setIsLoadingBucketUrl(false);
    }
  };

  const getSelectionSummary = () => {
    const participantCount = selectedParticipants.length;
    const dataTypeCount = selectedDataTypes.length;
    const { startDate, endDate, mode } = dateRange;

    let dateCount = 0;
    if (mode === 'single' && startDate) dateCount = 1;
    else if (mode === 'range' && startDate && endDate) {
      dateCount = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }

    const estimatedApiCalls = participantCount * dataTypeCount * dateCount;

    return {
      participantCount,
      dataTypeCount,
      dateCount,
      estimatedApiCalls
    };
  };

  const summary = getSelectionSummary();
  const stepAccordionSx = {
    mb: 1,
    '&:before': { display: 'none' },
    borderRadius: source ? '16px' : 2,
    ...(source && { border: '1px solid #e7e8ec', boxShadow: '0 3px 16px rgba(24, 32, 48, 0.035)' }),
    overflow: 'hidden',
    '& .MuiAccordionSummary-root': {
      px: { xs: 2.5, sm: 4 },
      minHeight: 64,
    },
    '& .MuiAccordionSummary-content': {
      my: 1.25,
    },
    '& .MuiAccordionDetails-root': {
      px: { xs: 2.5, sm: 4 },
      pb: 2.5,
      pt: 1,
    },
  };

  return (
    <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" display="flex" alignItems="center" gap={1}>
            <DataUsage color="primary" />
            {dataSource === 'fitbit' ? 'Fitbit' : 'HealthKit'} Data Extraction
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph sx={{ textAlign: 'center' }}>
          Extract participant {dataSource === 'fitbit' ? 'Fitbit' : dataSource === 'healthkit' ? 'HealthKit' : 'EHR (Health Records)'} data for analysis. Select one or more participants, data types, and a date range below. Each participant gets a separate extraction.
        </Typography>

        <Box display="grid" gridTemplateColumns={source ? '1fr' : { xs: '1fr', lg: '2fr 1fr' }} gap={3}>
          {/* Configuration Panel - Wizard-Style Accordions */}
          <Box sx={{ px: source ? 0 : { xs: 1, sm: 2 }, py: 0.5 }}>
            {/* Step 1: Data Source */}
            {!source && <Accordion
              expanded={expandedAccordions.has('dataSource')}
              onChange={() => toggleAccordion('dataSource')}
              sx={stepAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ fontWeight: 600 }}>1. Select Data Source</Typography>
                  <Chip
                    icon={dataSource === 'fitbit'
                      ? <img src="/Fitbit-Logo.png" alt="Fitbit" style={{ width: 16, height: 16 }} />
                      : <Apple sx={{ color: '#555555 !important', fontSize: 16 }} />}
                    label={dataSource === 'fitbit' ? 'Fitbit' : 'HealthKit'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Fitbit card */}
                  <Box
                    onClick={() => {
                      if (dataSource !== 'fitbit') {
                        setDataSource('fitbit');
                        setSelectedDataTypes([]);
                        setExpandedAccordions(prev => {
                          const next = new Set(prev);
                          next.add('participants');
                          return next;
                        });
                      }
                    }}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      p: 2.5,
                      border: '2px solid',
                      borderColor: dataSource === 'fitbit' ? '#00B0B9' : 'divider',
                      borderRadius: 2,
                      cursor: 'pointer',
                      backgroundColor: dataSource === 'fitbit' ? 'rgba(0, 176, 185, 0.04)' : 'transparent',
                      boxShadow: dataSource === 'fitbit' ? 3 : 0,
                      transform: dataSource === 'fitbit' ? 'scale(1.03)' : 'scale(1)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: '#00B0B9',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <img src="/Fitbit-Logo.png" alt="Fitbit" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Fitbit
                    </Typography>
                  </Box>

                  {/* HealthKit card */}
                  <Box
                    onClick={() => {
                      if (dataSource !== 'healthkit') {
                        setDataSource('healthkit');
                        setSelectedDataTypes([]);
                        setExpandedAccordions(prev => {
                          const next = new Set(prev);
                          next.add('participants');
                          return next;
                        });
                      }
                    }}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      p: 2.5,
                      border: '2px solid',
                      borderColor: dataSource === 'healthkit' ? '#555555' : 'divider',
                      borderRadius: 2,
                      cursor: 'pointer',
                      backgroundColor: dataSource === 'healthkit' ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                      boxShadow: dataSource === 'healthkit' ? 3 : 0,
                      transform: dataSource === 'healthkit' ? 'scale(1.03)' : 'scale(1)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: '#555555',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Apple sx={{ fontSize: 48, color: '#555555' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      HealthKit
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>}

            {/* Step 2: Participants (replaces the inline section) */}
            <Accordion
              expanded={expandedAccordions.has('participants')}
              onChange={() => toggleAccordion('participants')}
              sx={stepAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ fontWeight: 600 }}>2. Select Participants</Typography>
                  {selectedParticipants.length > 0 && (
                    <Chip
                      icon={<CheckCircle />}
                      label={`${selectedParticipants.length} selected`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>

              {/* Participant Selection */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">Select Participants</Typography>
                  <Box>
                    <Button size="small" onClick={() => chooseParticipants(authorizedParticipants.map(item => item.participantId))} disabled={isExtracting || !authorizedParticipants.length}>Select all authorized</Button>
                    <Button size="small" onClick={clearParticipantSelection} disabled={isExtracting}>
                      Clear
                    </Button>
                  </Box>
                </Box>

                <FormControl fullWidth>
                  <InputLabel id={`${participantSelectId}-label`}>Participants</InputLabel>
                  <Select
                    multiple
                    labelId={`${participantSelectId}-label`}
                    value={selectedParticipants}
                    onChange={handleParticipantChange}
                    input={<OutlinedInput label="Participants" />}
                    renderValue={selected => `${selected.length} ${selected.length === 1 ? 'participant' : 'participants'} selected`}
                    disabled={isExtracting}
                  >
                    {authorizedParticipants.map((participant) => {
                      const displayName = participant.firstName && participant.lastName
                        ? `${participant.firstName} ${participant.lastName}`
                        : participant.firstName || participant.lastName || participant.participantId;

                      const statusLine: string[] = [];
                      if (participant.participantEmail) statusLine.push(participant.participantEmail);
                      statusLine.push(participant.authorizationStatus ? 'Authorized' : 'Not Authorized');

                      const dateRangeLine = buildHistoricalAvailabilityLine(participant);

                      return (
                        <MenuItem key={participant.id} value={participant.participantId}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1,
                              width: '100%',
                            }}
                          >
                            <ListItemText 
                            primary={displayName}
                            secondary={
                              <>
                                <Typography component="span" variant="body2" display="block">{statusLine.join(' • ')}</Typography>
                                <Typography component="span" variant="body2" display="block" color="text.secondary">{displayName}: {dateRangeLine}</Typography>
                              </>
                            }
                            />
                            <CheckCircle
                              color={selectedParticipants.includes(participant.participantId) ? 'success' : 'disabled'}
                              sx={{
                                mt: 0.25,
                                opacity: selectedParticipants.includes(participant.participantId) ? 1 : 0,
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {authorizedParticipants.length === 0 && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    No participant in this study with {dataSource === 'fitbit' ? 'Fitbit' : 'HealthKit'}.
                  </Alert>
                )}
                {selectedParticipants.length > 0 && (() => {
                  return (
                    <Box sx={{ mt: 1.5, pl: 1.5, py: 1, maxHeight: 130, overflowY: 'auto', borderLeft: '3px solid', borderColor: 'divider' }}>
                      {authorizedParticipants.filter(p => selectedParticipants.includes(p.participantId)).map(participant => {
                        const line = buildHistoricalAvailabilityLine(participant);
                        const name = participant.firstName && participant.lastName ? `${participant.firstName} ${participant.lastName}` : participant.participantId;
                        return (
                          <Typography key={participant.participantId} variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                            {name}: {line}
                          </Typography>
                        );
                      })}
                    </Box>
                  );
                })()}
              </Box>
              </AccordionDetails>
            </Accordion>

            {/* Step 3: Date Range & Filters */}
            <Accordion
              expanded={expandedAccordions.has('dateRange')}
              onChange={() => toggleAccordion('dateRange')}
              sx={stepAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ fontWeight: 600 }}>3. Date Range & Filters</Typography>
                  {dateRange.startDate && (
                    <Chip
                      icon={<CheckCircle />}
                      label={dateRange.mode === 'range' && dateRange.endDate
                        ? `${dateRange.startDate.toISOString().split('T')[0]} to ${dateRange.endDate.toISOString().split('T')[0]}`
                        : dateRange.startDate.toISOString().split('T')[0]}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
                    Select Date Range
                    {dateRestrictions.maxDate && (
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                        (up to {dateRestrictions.maxDate.toISOString().split('T')[0]})
                      </Typography>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<InfoOutlined />}
                      onClick={() => setShowDateTips(!showDateTips)}
                      disabled={selectedParticipants.length === 0}
                    >
                      Tips
                    </Button>
                  </Box>
                </Box>

                {selectedParticipants.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Choose one or more participants to see their historical date range.
                  </Alert>
                )}

                {/* Collapsible tips */}
                <Collapse in={showDateTips}>
                  <Box sx={{ mb: 2 }}>
                    {dateRestrictions.maxDate && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          Data available up to yesterday ({dateRestrictions.maxDate.toISOString().split('T')[0]}).
                          Fitbit data typically has a 1-day delay. All dates are in UTC.
                        </Typography>
                      </Alert>
                    )}
                    {selectedParticipantAvailabilityRange.startDate && selectedParticipantAvailabilityRange.endDate && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          Selected participants have historical extraction availability spanning {selectedParticipantAvailabilityRange.startDate} to {selectedParticipantAvailabilityRange.endDate}. Each file contains only that participant&apos;s data.
                        </Typography>
                      </Alert>
                    )}
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Note:</strong> Due to rate limits, we can only extract 30 days of data per hour. For up to 2 years of historical data, extraction may take up to 24 hours.
                      </Typography>
                    </Alert>
                  </Box>
                </Collapse>

                <DateRangeControl key={studyId + ':' + dataSource + ':' + selectedParticipants.join(',')}
                  value={{ startDate: dateRange.startDate ? toDateInputValue(dateRange.startDate) : '',
                    endDate: dateRange.mode === 'single' ? dateRange.startDate ? toDateInputValue(dateRange.startDate) : '' : dateRange.endDate ? toDateInputValue(dateRange.endDate) : '' }}
                  onChange={range => setDateRange({ startDate: range.startDate ? new Date(range.startDate + 'T00:00:00Z') : null,
                    endDate: range.endDate ? new Date(range.endDate + 'T00:00:00Z') : null, mode: 'range' })}
                  available={{ startDate: selectedParticipantAvailabilityRange.startDate || undefined, endDate: selectedParticipantAvailabilityRange.endDate || undefined }}
                  minDate={selectedParticipantAvailabilityRange.startDate || undefined}
                  maxDate={selectedParticipantAvailabilityRange.endDate || dateRestrictions.maxDate?.toISOString().split('T')[0]}
                  startLabel="Start Date" endLabel="End Date" disabled={isExtracting || selectedParticipants.length === 0}
                  referenceDescription="Dates span the selected participants’ histories. Choose the same start and end date for a single day." />
              </AccordionDetails>
            </Accordion>

            {/* Step 4: Data Types */}
            <Accordion
              expanded={expandedAccordions.has('dataTypes')}
              onChange={() => toggleAccordion('dataTypes')}
              sx={stepAccordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ fontWeight: 600 }}>4. Select Data Types</Typography>
                  {selectedDataTypes.length > 0 && (
                    <Chip
                      icon={<CheckCircle />}
                      label={`${selectedDataTypes.length} selected`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1">
                    {dataSource === 'fitbit' ? 'Fitbit' : 'HealthKit'} Data Types
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleToggleAllDataTypes}
                    disabled={isExtracting || dataTypeValues.length === 0}
                  >
                    {allDataTypesSelected ? 'Clear All' : 'Check All'}
                  </Button>
                </Box>
                <FormControl fullWidth>
                  <InputLabel>Data Types</InputLabel>
                  <Select
                    multiple
                    value={selectedDataTypes}
                    onChange={handleDataTypeChange}
                    input={<OutlinedInput label="Data Types" />}
                    renderValue={(selected) => `${selected.length} data types selected`}
                    disabled={isExtracting}
                  >
                    {(() => {
                      // Group metrics by section
                      const sections: { [key: string]: typeof dataTypes } = {};
                      dataTypes.forEach(dataType => {
                        const section = (dataType as any).section || 'Other';
                        if (!sections[section]) sections[section] = [];
                        sections[section].push(dataType);
                      });

                      return Object.entries(sections).map(([sectionName, metrics]) => [
                        // Section Header
                        <MenuItem key={`header-${sectionName}`} disabled sx={{ py: 0, fontWeight: 'bold', fontSize: '0.9rem', backgroundColor: '#f5f5f5', color: '#333' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', width: '100%', py: 1, px: 1 }}>
                            {sectionName}
                          </Typography>
                        </MenuItem>,
                        // Metrics in this section
                        ...metrics.map((dataType) => (
                          <MenuItem key={dataType.value} value={dataType.value} sx={{ pl: 4 }}>
                            <Checkbox checked={selectedDataTypes.indexOf(dataType.value) > -1} />
                            <ListItemText
                              primary={dataType.label}
                              secondary={dataType.description}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </MenuItem>
                        ))
                      ]).flat();
                    })()}
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Summary & Actions Panel */}
          <Box>
            {/* The combined export page provides bucket access in its header. */}
            {!source && <Paper sx={{ p: 2, mb: 2 }}>
              {showBucketHelp && (
                <Alert
                  severity="info"
                  onClose={() => {
                    setShowBucketHelp(false);
                    localStorage.setItem(`gcs_help_dismissed_${studyId}`, 'true');
                  }}
                  sx={{ mb: 1.5 }}
                >
                  Your extracted data files are stored in a Google Cloud Storage bucket.
                </Alert>
              )}
              <Tooltip title="Opens your study's data export bucket in Google Cloud Storage" arrow>
                <span>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={isLoadingBucketUrl ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Cloud sx={{ color: 'white' }} />}
                    endIcon={<OpenInNew sx={{ fontSize: 14, color: 'white' }} />}
                    onClick={handleGoToBucket}
                    disabled={!bucketName || isLoadingBucketUrl}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #8C1515 0%, #5C0E0E 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5C0E0E 0%, #3A0909 100%)',
                      },
                      '&.Mui-disabled': {
                        background: '#bdbdbd',
                      },
                    }}
                  >
                    {isLoadingBucketUrl ? 'Loading...' : 'Open in Google Cloud'}
                  </Button>
                </span>
              </Tooltip>
            </Paper>}

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Extraction Summary
              </Typography>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Participants:</Typography>
                <Typography variant="h6">{summary.participantCount}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Data Types:</Typography>
                <Typography variant="h6">{summary.dataTypeCount}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Date(s):</Typography>
                <Typography variant="h6">{summary.dateCount}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Email Notification Option */}
              <Box mb={2}>
                <FormControl fullWidth>
                  <Box display="flex" alignItems="center" sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                    <Checkbox
                      checked={notifyOnCompletion}
                      onChange={(e) => setNotifyOnCompletion(e.target.checked)}
                      disabled={isExtracting}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Notify me when extraction completes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        You'll receive an email at {user?.email || 'your email'} when the extraction finishes
                      </Typography>
                    </Box>
                  </Box>
                </FormControl>
              </Box>

              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleStartExtraction}
                  disabled={isExtracting || summary.estimatedApiCalls === 0}
                  startIcon={isExtracting ? <CircularProgress size={20} /> : <GetApp />}
                >
                  {isExtracting ? 'Extracting...' : 'Start Extraction'}
                </Button>

                <Tooltip title="Preview extraction plan">
                  <span>
                    <IconButton
                      onClick={() => setPreviewDialogOpen(true)}
                      disabled={summary.estimatedApiCalls === 0}
                    >
                      <Info />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Paper>

            {/* Inline Extraction Jobs Panel */}
            <Paper
              sx={{
                mt: 3,
                p: 3,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
                border: '1px solid #e3e8ef',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    Extraction Jobs
                  </Typography>
                  {isLoadingJobs && (
                    <CircularProgress size={18} sx={{ color: 'primary.main' }} />
                  )}
                </Box>
                <Tooltip title="Refresh extraction jobs">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Refresh extraction jobs"
                      aria-busy={isRefreshingJobs}
                      disabled={isLoadingJobs || isRefreshingJobs}
                      onClick={() => void loadJobsFromFirestore(true)}
                    >
                      {isRefreshingJobs
                        ? <CircularProgress size={20} />
                        : <Refresh fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {jobsRefreshError && (
                <Alert
                  severity="error"
                  onClose={() => setJobsRefreshError('')}
                  sx={{ mb: 2 }}
                >
                  {jobsRefreshError}
                </Alert>
              )}

              {/* Status chips */}
              <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" mb={2}>
                {extractionJobs.filter(j => j.status === 'running').length > 0 && (
                  <Chip
                    icon={<CircularProgress size={18} sx={{ color: 'white !important' }} />}
                    label={`${extractionJobs.filter(j => j.status === 'running').length} Running`}
                    size="medium"
                    sx={{
                      backgroundColor: '#1976d2',
                      color: 'white',
                      fontWeight: 500,
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                )}
                {extractionJobs.filter(j => j.status === 'completed').length > 0 && (
                  <Chip
                    icon={<CheckCircle sx={{ color: 'white !important' }} />}
                    label={`${extractionJobs.filter(j => j.status === 'completed').length} Completed`}
                    size="medium"
                    sx={{
                      backgroundColor: '#2e7d32',
                      color: 'white',
                      fontWeight: 500,
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                )}
                {extractionJobs.filter(j => j.status === 'failed').length > 0 && (
                  <Chip
                    icon={<ErrorIcon sx={{ color: 'white !important' }} />}
                    label={`${extractionJobs.filter(j => j.status === 'failed').length} Failed`}
                    size="medium"
                    sx={{
                      backgroundColor: '#d32f2f',
                      color: 'white',
                      fontWeight: 500,
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                )}
              </Box>

              {/* Tier-1: Inline running jobs (always visible) */}
              {isLoadingJobs ? (
                <Box>
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 0.5 }} />
                  ))}
                </Box>
              ) : (
                <>
                  {extractionJobs
                    .filter(j => j.status === 'running')
                    .map(job => (
                      <Box
                        key={job.id}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setSelectedJobForDetail(job);
                          setDetailDialogOpen(true);
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                              {job.extractionName}
                            </Typography>
                          </Box>
                        </Box>
                        <ExtractionJobProgressIndicator job={job} compact />
                      </Box>
                    ))}

                  {/* Tier-2: Recent completed/failed jobs */}
                  {(() => {
                    const recentJobs = extractionJobs
                      .filter(j => j.status === 'completed' || j.status === 'failed')
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                    const displayedJobs = showAllJobs ? recentJobs : recentJobs.slice(0, 5);

                    return (
                      <>
                        {displayedJobs.map(job => (
                          <Box
                            key={job.id}
                            sx={{
                              p: 1.5,
                              mb: 0.5,
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'grey.50' },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                            onClick={() => {
                              setSelectedJobForDetail(job);
                              setDetailDialogOpen(true);
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                              {job.status === 'completed' ? (
                                <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                              ) : (
                                <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
                              )}
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {job.extractionName}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {new Date(job.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        ))}
                        {recentJobs.length > 5 && (
                          <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Button
                              variant="text"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (recentJobs.length > 20) {
                                  setJobsDialogOpen(true);
                                } else {
                                  setShowAllJobs(!showAllJobs);
                                }
                              }}
                            >
                              {showAllJobs
                                ? 'Show less'
                                : `Show all ${recentJobs.length} jobs`}
                            </Button>
                          </Box>
                        )}
                      </>
                    );
                  })()}

                  {extractionJobs.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No extraction jobs yet
                    </Typography>
                  )}

                  {/* Full dialog for all jobs (Tier-3) */}
                  {extractionJobs.length > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => setJobsDialogOpen(true)}
                      >
                        View All Jobs ({extractionJobs.length})
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Extraction Naming Dialog */}
        <Dialog
          open={nameDialogOpen}
          onClose={() => setNameDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Name Your Extraction</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              Give this extraction a descriptive name for easy identification in your extraction history.
            </Typography>

            <TextField
              autoFocus
              margin="dense"
              label="Extraction Name"
              fullWidth
              variant="outlined"
              value={extractionName}
              onChange={(e) => setExtractionName(e.target.value)}
              placeholder="e.g., Baseline_HeartRate_Week1"
              sx={{ mt: 2 }}
            />

            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Summary:</strong> {summary.participantCount} participant{summary.participantCount === 1 ? '' : 's'}, {summary.dataTypeCount} data types, {summary.estimatedApiCalls} API calls
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleConfirmExtraction} 
              variant="contained"
              disabled={!extractionName.trim()}
            >
              Start Extraction
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Extraction Preview</DialogTitle>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              Selected Configuration:
            </Typography>

            <Box mb={2}>
              <Typography variant="subtitle2">Participant{summary.participantCount === 1 ? '' : 's'} ({summary.participantCount}):</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {selectedParticipants.slice(0, 5).map(id => {
                  const participant = participants.find(p => p.participantId === id);
                  const displayName = participant?.firstName && participant?.lastName
                    ? `${participant.firstName} ${participant.lastName}`
                    : participant?.firstName || participant?.lastName || participant?.participantId || id;
                  return (
                    <Chip
                      key={id}
                      label={displayName}
                      size="small"
                    />
                  );
                })}
                {selectedParticipants.length > 5 && (
                  <Chip label={`+${selectedParticipants.length - 5} more`} size="small" />
                )}
              </Box>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2">Data Types ({summary.dataTypeCount}):</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {selectedDataTypes.slice(0, 5).map(type => {
                  const dataType = dataTypes.find(dt => dt.value === type);
                  return (
                    <Chip
                      key={type}
                      label={dataType?.label || type}
                      size="small"
                      color="primary"
                    />
                  );
                })}
                {selectedDataTypes.length > 5 && (
                  <Chip label={`+${selectedDataTypes.length - 5} more`} size="small" color="primary" />
                )}
              </Box>
            </Box>

            <Alert severity="info">
              {dataSource === 'fitbit'
                ? <>This will make approximately <strong>{summary.estimatedApiCalls} API calls</strong> to Fitbit's servers.</>
                : <>This will query the HealthKit database for <strong>{summary.estimatedApiCalls} data {summary.estimatedApiCalls === 1 ? 'type' : 'types'}</strong>.</>
              }
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Job Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Extraction Details</DialogTitle>
          <DialogContent>
            {selectedJobForDetail && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedJobForDetail.extractionName}
                </Typography>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Date Range:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateRange(selectedJobForDetail.dateRange)}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Participant:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const participant = participants.find(p => p.participantId === selectedJobForDetail.participantId);
                      if (participant?.firstName && participant?.lastName) {
                        return `${participant.firstName} ${participant.lastName}`;
                      }
                      if (participant?.firstName || participant?.lastName) {
                        return participant.firstName || participant.lastName || selectedJobForDetail.participantId;
                      }
                      return selectedJobForDetail.participantId;
                    })()}
                  </Typography>
                  {(() => {
                    const participant = participants.find(p => p.participantId === selectedJobForDetail.participantId);
                    if (participant && (participant.participantEmail || participant.phoneNumber)) {
                      return (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {participant.participantEmail && `Email: ${participant.participantEmail}`}
                          {participant.participantEmail && participant.phoneNumber && " • "}
                          {participant.phoneNumber && `Phone: ${participant.phoneNumber}`}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Data Types ({selectedJobForDetail.dataTypes.length}):</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {selectedJobForDetail.dataTypes.map(type => {
                      const dataType = dataTypes.find(dt => dt.value === type);
                      return (
                        <Chip
                          key={type}
                          label={dataType?.label || type}
                          size="small"
                          color="primary"
                        />
                      );
                    })}
                  </Box>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Status:</Typography>
                  <Chip
                    icon={getJobStatusIcon(selectedJobForDetail.status)}
                    label={selectedJobForDetail.status}
                    color={getJobStatusColor(selectedJobForDetail.status) as any}
                  />
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Created:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedJobForDetail.createdAt.toLocaleString()}
                  </Typography>
                </Box>

                {selectedJobForDetail.completedAt && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>Completed:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedJobForDetail.completedAt.toLocaleString()}
                    </Typography>
                  </Box>
                )}

                {selectedJobForDetail.error && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>Error:</Typography>
                    <Alert severity="error">
                      {selectedJobForDetail.error}
                    </Alert>
                  </Box>
                )}

                {Array.isArray(selectedJobForDetail.failedTypes) && selectedJobForDetail.failedTypes.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>Data Types That Could Not Be Extracted:</Typography>
                    <Alert severity="warning">
                      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                        {`The following ${selectedJobForDetail.failedTypes.length} data type(s) failed during extraction. Other data types completed successfully.`}
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {selectedJobForDetail.failedTypes.map((ft, idx) => (
                          <li key={`${ft.type}-${idx}`}>
                            <Typography variant="caption" component="span" fontWeight={600}>{ft.type}</Typography>
                            {ft.reason ? <Typography variant="caption" component="span" color="text.secondary">{`: ${ft.reason}`}</Typography> : null}
                          </li>
                        ))}
                      </Box>
                    </Alert>
                  </Box>
                )}

              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                if (selectedJobForDetail) {
                  handleDeleteJob(selectedJobForDetail);
                }
              }}
              color="error"
              startIcon={<Delete />}
              disabled={isDeleting}
            >
              Delete
            </Button>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Extraction Job</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Are you sure you want to delete this extraction job?
            </Typography>
            {jobToDelete && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Job:</strong> {jobToDelete.extractionName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Participant:</strong> {jobToDelete.participantId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Date Range:</strong> {formatDateRange(jobToDelete.dateRange)}
                </Typography>
              </Box>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. The job will be permanently deleted.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setDeleteConfirmOpen(false);
                setJobToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteJob}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={20} /> : <Delete />}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* All Extraction Jobs Dialog */}
        <Dialog
          open={jobsDialogOpen}
          onClose={() => setJobsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">All Extraction Jobs</Typography>
              <Box display="flex" gap={1} alignItems="center">
                {extractionJobs.filter(j => j.status === 'running').length > 0 && (
                  <Chip
                    icon={<CircularProgress size={16} />}
                    label={`${extractionJobs.filter(j => j.status === 'running').length} Running`}
                    size="small"
                    color="primary"
                  />
                )}
                {extractionJobs.filter(j => j.status === 'completed').length > 0 && (
                  <Chip
                    icon={<CheckCircle />}
                    label={`${extractionJobs.filter(j => j.status === 'completed').length} Completed`}
                    size="small"
                    color="success"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  ({extractionJobs.length} total)
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {extractionJobs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
                No extraction jobs yet. Start an extraction to see jobs here.
              </Typography>
            ) : (
              <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                {extractionJobs.slice().reverse().map((job) => {
                  const participant = participants.find(p => p.participantId === job.participantId);
                  const participantName = participant 
                    ? `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || participant.participantId
                    : job.participantId;

                  return (
                    <ListItem
                      key={job.id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: job.status === 'running' ? '#f5f5f5' : 'white',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" width="100%">
                        <ListItemIcon sx={{ minWidth: 40, pt: 0.5 }}>
                          {job.status === 'running' ? (
                            <CircularProgress size={24} />
                          ) : job.status === 'completed' ? (
                            <CheckCircle color="success" />
                          ) : job.status === 'failed' ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <Schedule color="action" />
                          )}
                        </ListItemIcon>
                        <Box flex={1} minWidth={0}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                            <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                              <Typography variant="body1" fontWeight="medium" noWrap>
                                {job.extractionName}
                              </Typography>
                              <Chip
                                label={job.status}
                                size="small"
                                color={getJobStatusColor(job.status) as any}
                              />
                            </Box>
                            <Box display="flex" gap={1} alignItems="center" flexShrink={0}>
                              <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => {
                                  handleShowJobDetail(job);
                                  setJobsDialogOpen(false);
                                }}
                                variant="outlined"
                              >
                                View Detail
                              </Button>
                              {dataSource === 'fitbit' && job.status === 'completed' && job.jobId && (
                                <Button
                                  size="small"
                                  startIcon={downloadingJobId === job.jobId
                                    ? <CircularProgress size={16} />
                                    : <GetApp />}
                                  onClick={(e) => handleDownloadMenuOpen(e, job)}
                                  variant="outlined"
                                  disabled={downloadingJobId !== null}
                                  aria-busy={downloadingJobId === job.jobId}
                                  aria-controls={downloadMenuAnchor ? 'download-menu' : undefined}
                                  aria-haspopup="true"
                                  aria-expanded={downloadMenuAnchor ? 'true' : undefined}
                                >
                                  {downloadingJobId === job.jobId ? 'Downloading...' : 'Download'}
                                  {downloadingJobId !== job.jobId && (
                                    <ExpandMore sx={{ ml: 0.25, fontSize: 18 }} />
                                  )}
                                </Button>
                              )}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteJob(job)}
                                disabled={isDeleting}
                                title="Delete job"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      <ListItemText
                        sx={{ ml: 5 }}
                        primary={null}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Participant: {participantName}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Date Range: {formatDateRange(job.dateRange)}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Data Types: {job.dataTypes.length} selected
                            </Typography>
                            {job.status === 'running' && (
                              <Box mt={2} pt={1.5} sx={{ borderTop: '1px solid #e0e0e0' }}>
                                <ExtractionJobProgressIndicator job={job} />
                              </Box>
                            )}
                            {job.status === 'failed' && job.error && (
                              <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
                                <Typography variant="caption">
                                  {job.error}
                                </Typography>
                              </Alert>
                            )}
                            {job.status === 'completed' && Array.isArray(job.failedTypes) && job.failedTypes.length > 0 && (
                              <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
                                <Typography variant="caption" component="span" fontWeight={600}>
                                  {`${job.failedTypes.length} data type(s) could not be extracted: `}
                                </Typography>
                                <Typography variant="caption" component="span">
                                  {job.failedTypes.map((ft, idx) =>
                                    idx === 0 ? ft.type : `, ${ft.type}`
                                  )}
                                </Typography>
                              </Alert>
                            )}
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: job.status === 'running' ? 1.5 : 0.5 }}>
                              Created: {job.createdAt.toLocaleString()}
                              {job.completedAt && ` • Completed: ${job.completedAt.toLocaleString()}`}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <Menu
            id="download-menu"
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={handleDownloadMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleDownload('json')}>Download as JSON</MenuItem>
            <MenuItem onClick={() => handleDownload('zip')}>Download as ZIP</MenuItem>
          </Menu>
          <DialogActions>
            <Button onClick={() => setJobsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default DataExtractionInterface;
